import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import BoardThree from '../components/WorkingChessLogic/3Board.jsx';
import ControlsThree from '../components/WorkingChessLogic/3Controls.jsx';
import { useGameState } from '../components/WorkingChessLogic/GameStateContext.js';
import useGameLogic from '../components/WorkingChessLogic/UseGameState.js';

import ScreamSkeleton from '../util/ScreamSkeleton';

import { connect } from 'react-redux';
import { getScreams } from '../redux/actions/dataActions';
// import Board from '../components/chess/Board';
import Board, {
  FENBuilderModal,
} from '../components/WorkingChessLogic/3Board.jsx';

import MovesThree from '../components/WorkingChessLogic/3Moves.jsx';
import SetupDefaults from '../components/WorkingChessLogic/SetupDefaults.js';

import { Chess } from 'chess.js';
import Controls from '../components/ArchivedChess/Controls.jsx';
import Moves from '../components/ArchivedChess/Moves.jsx';
import useMoveIndex from '../hooks/useMoveIndex';
import { postGame } from '../util/api/game';
import {
  getFlippedBoardOrientation,
  getMove,
  getMoveAndVariationIndices,
  getTurnInFen,
  isMoveInVariation,
  isVariationMove,
  onNextMove,
  onPrevMove,
  getPgnFromMoves,
  getCurrentVariation,
  getParents,
  resetPositionCache,
} from '../util/Chess';
import Select from 'react-select';
import constants, {
  TAGS,
  DIFFICULTY,
  allowedEcoGroups,
} from '../util/constants';
import { ChessSocialContext } from '../util/ChessSocialContext';
import ChessAnalysisWrapper from '../util/ChessAnalysisWrapper';
import FAQAndSettingsModal from '../components/modals/FAQAndSettingsModal';
import DraftsModal from '../components/modals/DraftsModal';
import GameModeChooserModal from '../components/modals/GameModeChooserModal';
import StartPositionModal from '../components/modals/StartPositionModal';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import Stockfish from './Stockfish2';
import StockfishEval from '../components/ArchivedChess/StockfishEval.js';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  addDoc,
  setDoc,
} from 'firebase/firestore';
import { firestore } from '../firebase';

import StockfishAnalysisPanel from '../components/ArchivedChess/StockfishAnalysisPanel.jsx';
import PVLinesDisplay from '../components/ArchivedChess/PVLinesDisplay.jsx';

import { COLORS } from '../constants/colors';

import BrilliantGray from '../images/FooterIcons/Brilliance2.png';
import BrilliantBlue from '../images/FooterIcons/Brilliance4.png';
import UndoConfirmModal from '../components/modals/UndoConfirmModal.jsx';
import { isProfane, cleanProfanity } from '../util/filterProfanity';
import { checkRateLimit } from '../util/rateLimiter';
import { getRateLimit, buildGlobalRateKey } from '../util/rateLimitConfig';
import RateLimitModal from '../components/modals/RateLimitModal';
import StartingVariationLimitModal from '../components/modals/StartingVariationLimitModal';

// Add import for ChessAnalysisContext
import { useChessAnalysis } from '../util/ChessAnalysisContext';
// import PuzzleControls from '../components/chess/PuzzleControls';
import { createStartPosition } from '../util/PositionData';
import { getCurrentBoardPosition } from '../components/WorkingChessLogic/UseGameState.js';

// Add the new import at the top with other imports
import useStockfishAnalysisManager from '../hooks/useStockfishAnalysisManager';

const VISIBILITIES = constants.VISIBILITIES;
const CHESSBOARD = constants.CHESSBOARD;

// Create custom styles for the component
const customStyles = {
  placeholderRed: {
    '::placeholder': {
      color: '#ff0050',
    },
  },
};

// Add custom CSS to document once
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  .placeholder-red::placeholder {
    color: #ff0050 !important;
  }
  select {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white' width='18px' height='18px'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 20px center;
    padding-right: 40px !important;
  }
`;
document.head.appendChild(styleSheet);

// Deep sanitization helper to ensure Firestore compatibility
const deepSanitizeForFirestore = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (
    typeof obj === 'string' ||
    typeof obj === 'number' ||
    typeof obj === 'boolean'
  )
    return obj;

  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== undefined && item !== null) // Remove undefined/null items
      .map((item) => deepSanitizeForFirestore(item));
  }

  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Skip invalid keys or values
      if (!key || typeof key !== 'string') continue;
      if (value === undefined || value === null) continue;

      // Skip functions, symbols, and complex objects that Firestore can't handle
      if (typeof value === 'function' || typeof value === 'symbol') continue;
      if (value instanceof Date) {
        sanitized[key] = value.toISOString();
        continue;
      }
      if (
        value instanceof RegExp ||
        value instanceof Map ||
        value instanceof Set
      )
        continue;
      if (value instanceof Error) continue; // Skip error objects

      // Skip objects with circular references or prototypes
      if (
        typeof value === 'object' &&
        value.constructor &&
        value.constructor !== Object &&
        value.constructor !== Array
      ) {
        continue;
      }

      sanitized[key] = deepSanitizeForFirestore(value);
    }
    return sanitized;
  }

  return obj;
};

// Function to sanitize startPosition data for Firestore submission
const sanitizeStartPositionForFirestore = (startPosition) => {
  if (!startPosition) return null;

  // console.log('🔧 SANITIZING startPosition for Firestore:', {
  //   original: startPosition,
  //   hasVariations: Array.isArray(startPosition.variations),
  //   variationsLength: startPosition.variations?.length || 0,
  //   variationsDetail: startPosition.variations?.map((v) =>
  //     v?.map((m) => ({
  //       notation: m?.notation,
  //       hasComplexProps: Object.keys(m || {}).length,
  //     })),
  //   ),
  // });

  // Create a clean copy with only essential primitive data
  const basicSanitized = {
    fen: String(
      startPosition.fen ||
        'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    ),
    content: String(startPosition.content || ''),

    // Include variation captions for start position variations
    ...(startPosition.variationCaptions &&
      typeof startPosition.variationCaptions === 'object' && {
        variationCaptions: Object.fromEntries(
          Object.entries(startPosition.variationCaptions)
            .filter(
              ([key, value]) =>
                typeof key === 'string' && typeof value === 'string',
            )
            .map(([key, value]) => [String(key), String(value)]),
        ),
      }),

    // Only include arrows if they exist and are simple
    ...(Array.isArray(startPosition.arrows) &&
      startPosition.arrows.length > 0 && {
        arrows: startPosition.arrows
          .filter(
            (arrow) =>
              arrow &&
              // Handle array format [from, to, color]
              ((Array.isArray(arrow) && arrow.length >= 2) ||
                // Handle object format {from, to, color}
                (typeof arrow === 'object' && arrow.from && arrow.to)),
          )
          .map((arrow) => {
            // Convert array format to object format
            if (Array.isArray(arrow)) {
              return {
                from: String(arrow[0]),
                to: String(arrow[1]),
                color: String(arrow[2] || 'green'),
              };
            }
            // Handle object format
            return {
              from: String(arrow.from),
              to: String(arrow.to),
              color: String(arrow.color || 'green'),
            };
          }),
      }),

    // Only include highlighted squares if they exist and are simple
    ...(startPosition.highlightedSquares &&
      typeof startPosition.highlightedSquares === 'object' && {
        highlightedSquares: Object.fromEntries(
          Object.entries(startPosition.highlightedSquares)
            .filter(
              ([key, value]) =>
                typeof key === 'string' && value && typeof value === 'object',
            )
            .map(([key, value]) => [
              String(key),
              {
                backgroundColor: String(value.backgroundColor || '#ff0050'),
              },
            ]),
        ),
      }),
  };

  // Handle variations separately to avoid syntax issues
  const processedVariations = Array.isArray(startPosition.variations)
    ? startPosition.variations
        .filter((variation) => Array.isArray(variation) && variation.length > 0)
        .map((variation) =>
          variation
            .filter((move) => move && typeof move === 'object' && move.notation)
            .map((move) => {
              // console.log(
              //   '🔧 [Sanitization] Processing move:',
              //   move.notation,
              //   'full move object:',
              //   move,
              // );
              return {
                // Essential move data
                notation: String(move.notation || ''),
                from: String(move.from || ''),
                to: String(move.to || ''),
                content: String(move.content || ''),

                // Include arrows if they exist and are simple
                ...(Array.isArray(move.arrows) &&
                  move.arrows.length > 0 && {
                    arrows: (() => {
                      // console.log(
                      //   '🔧 [Sanitization] Processing arrows for move:',
                      //   move.notation,
                      //   'arrows:',
                      //   move.arrows,
                      // );
                      const filteredArrows = move.arrows
                        .filter((arrow) => {
                          const isValidArray =
                            Array.isArray(arrow) && arrow.length >= 2;
                          const isValidObject =
                            typeof arrow === 'object' && arrow.from && arrow.to;
                          // console.log('🔧 [Sanitization] Arrow filter check:', {
                          //   arrow,
                          //   isValidArray,
                          //   isValidObject,
                          // });
                          return arrow && (isValidArray || isValidObject);
                        })
                        .map((arrow) => {
                          // console.log(
                          //   '🔧 [Sanitization] Processing arrow:',
                          //   arrow,
                          // );
                          // Convert array format to object format
                          if (Array.isArray(arrow)) {
                            const result = {
                              from: String(arrow[0]),
                              to: String(arrow[1]),
                              color: String(arrow[2] || 'green'),
                            };
                            // console.log(
                            //   '🔧 [Sanitization] Array arrow converted to:',
                            //   result,
                            // );
                            return result;
                          }
                          // Handle object format
                          const result = {
                            from: String(arrow.from),
                            to: String(arrow.to),
                            color: String(arrow.color || 'green'),
                          };
                          // console.log(
                          //   '🔧 [Sanitization] Object arrow converted to:',
                          //   result,
                          // );
                          return result;
                        });
                      // console.log(
                      //   '🔧 [Sanitization] Final arrows result:',
                      //   filteredArrows,
                      // );
                      return filteredArrows;
                    })(),
                  }),

                // Include highlighted squares if they exist and are simple
                ...(move.highlightedSquares &&
                  typeof move.highlightedSquares === 'object' && {
                    highlightedSquares: Object.fromEntries(
                      Object.entries(move.highlightedSquares)
                        .filter(
                          ([key, value]) =>
                            typeof key === 'string' &&
                            value &&
                            typeof value === 'object',
                        )
                        .map(([key, value]) => [
                          String(key),
                          {
                            backgroundColor: String(
                              value.backgroundColor || '#ff0050',
                            ),
                          },
                        ]),
                    ),
                  }),
              };
            }),
        )
    : [];

  // Add variations to the sanitized object
  const finalSanitized = {
    ...basicSanitized,
    variations: processedVariations,
  };

  // Apply deep sanitization to remove any remaining complex objects
  const sanitized = deepSanitizeForFirestore(finalSanitized);

  // Add comprehensive summary logging
  // console.log('✅ SANITIZED startPosition result:', {
  //   sanitized,
  //   hasVariations: Array.isArray(sanitized.variations),
  //   variationsLength: sanitized.variations?.length || 0,
  //   stringified: JSON.stringify(sanitized).substring(0, 200) + '...',
  // });

  // SUMMARY: Check what arrows made it through sanitization
  if (sanitized.variations && sanitized.variations.length > 0) {
    sanitized.variations.forEach((variation, varIdx) => {
      if (Array.isArray(variation)) {
        variation.forEach((move, moveIdx) => {
          if (move.arrows && move.arrows.length > 0) {
            // console.log(
            //   `✅ SANITIZATION SUCCESS: Variation ${varIdx} Move ${moveIdx} (${move.notation}) has ${move.arrows.length} arrows:`,
            //   move.arrows,
            // );
          } else {
            // console.log(
            //   `❌ SANITIZATION FAILED: Variation ${varIdx} Move ${moveIdx} (${move.notation}) has NO arrows`,
            // );
          }
        });
      }
    });
  }

  return sanitized;
};

const AddGame2 = (props) => {
  const containerRef = useRef(null);
  const boardRef = useRef(null); // Add boardRef here at the top with other refs
  const [boardSize, setBoardSize] = useState(500);
  const [openModalType, setOpenModalType] = useState(''); // Ensure this is at top level
  const history = useHistory();

  // Add modal states
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [undoTargetMove, setUndoTargetMove] = useState(null);
  const [showStartingVariationLimitModal, setShowStartingVariationLimitModal] = useState(false);

  const { state, dispatch } = useGameState();
  const { moves, rootFEN, starredMoves, orientation } = state;

  // Get necessary game functions from useGameLogic
  const { undoMove, jumpToLast } = useGameLogic();

  // Import ChessAnalysisContext for analysis functionality
  const {
    setCurrentPosition,
    setIsAnalyzing,
    updatePositionSilently,
    checkForExistingAnalysis,
  } = useChessAnalysis();

  // Add an effect to listen for the open-undo-modal event
  useEffect(() => {
    const handleOpenUndoModal = (event) => {
      //
      setUndoTargetMove(event.detail.currentMove);
      setShowUndoModal(true);
    };

    window.addEventListener('open-undo-modal', handleOpenUndoModal);

    return () => {
      window.removeEventListener('open-undo-modal', handleOpenUndoModal);
    };
  }, []);

  //

  useEffect(() => {
    if (moves.length > 0) {
      //
      //
    } else {
      //
    }
  }, [moves]);

  // +++ Get isPuzzleMode and setIsPuzzleMode from context +++
  const { isPuzzleMode, setIsPuzzleMode } = useContext(ChessSocialContext);

  // Expose puzzle mode globally for variation prevention
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.isPuzzleMode = isPuzzleMode;
    }
  }, [isPuzzleMode]);

  // State and refs for the chess control panel
  const [showControlPanel, setShowControlPanel] = useState(false);
  const boardIconRef = useRef(null);
  const controlPanelRef = useRef(null);

  // Initialize state from localStorage or default values
  const [localShowStockfish, setLocalShowStockfish] = useState(() => {
    // Try the 'showStockfish' key first (string format 'true'/'false')
    const simpleValue = localStorage.getItem('showStockfish');
    if (simpleValue !== null) {
      return simpleValue === 'true';
    }

    // Try the 'addGame_showStockfish' key next (JSON format)
    const jsonValue = localStorage.getItem('addGame_showStockfish');
    if (jsonValue !== null) {
      try {
        return JSON.parse(jsonValue);
      } catch (e) {
        console.error('Error parsing JSON from localStorage:', e);
      }
    }

    // Default to true if nothing found
    return true;
  });

  const [localShowStockfishEval, setLocalShowStockfishEval] = useState(() => {
    const savedValue = localStorage.getItem('addGame_showStockfishEval');
    return savedValue !== null ? JSON.parse(savedValue) : true;
  });

  const [localShowStockfishIcons, setLocalShowStockfishIcons] = useState(() => {
    // Use the same localStorage key as used in the toggle handler
    const savedValue = localStorage.getItem('showStockfishIcons');
    return savedValue === null ? false : savedValue === 'true';
  });

  // When values change, save to localStorage
  useEffect(() => {
    // Save to both keys for backward compatibility
    localStorage.setItem('showStockfish', localShowStockfish);
    localStorage.setItem(
      'addGame_showStockfish',
      JSON.stringify(localShowStockfish),
    );
  }, [localShowStockfish]);

  useEffect(() => {
    localStorage.setItem(
      'addGame_showStockfishEval',
      JSON.stringify(localShowStockfishEval),
    );
  }, [localShowStockfishEval]);

  useEffect(() => {
    // Use the same localStorage key as in the toggle handler
    localStorage.setItem('showStockfishIcons', localShowStockfishIcons);
  }, [localShowStockfishIcons]);

  // Close the control panel when clicking outside
  useEffect(() => {
    if (showControlPanel) {
      const handleClickOutside = (event) => {
        if (
          controlPanelRef.current &&
          !controlPanelRef.current.contains(event.target) &&
          boardIconRef.current &&
          !boardIconRef.current.contains(event.target)
        ) {
          setShowControlPanel(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showControlPanel]);

  // Function to determine the robot icon color based on active features
  const getRobotColor = () => {
    return localShowStockfish ||
      localShowStockfishEval ||
      localShowStockfishIcons ||
      showArrows
      ? 'rgb(76, 175, 80)'
      : '#ff0050';
  };

  // Add handler functions for the control panel buttons
  const handleStockfishToggle = () => {
    // Toggle the local state
    const newValue = !localShowStockfish;
    setLocalShowStockfish(newValue);

    // Synchronize with context state
    setShowStockfish(newValue);

    // Save to localStorage using a consistent key
    localStorage.setItem('showStockfish', newValue);
    localStorage.setItem('addGame_showStockfish', JSON.stringify(newValue));
  };

  const handleStockfishEvalToggle = () => {
    setLocalShowStockfishEval(!localShowStockfishEval);
  };

  const handleStockfishIconsToggle = () => {
    // Save to both local state and context
    const newValue = !localShowStockfishIcons;
    setLocalShowStockfishIcons(newValue);

    // Update context state which will affect the Board component
    setShowStockfishIcons(newValue);

    // Save to localStorage using both keys for consistency
    localStorage.setItem('showStockfishIcons', newValue);
    localStorage.setItem(
      'addGame_showStockfishIcons',
      JSON.stringify(newValue),
    );
  };

  useEffect(() => {
    const flipButtonStyleSheet = document.createElement('style');
    flipButtonStyleSheet.type = 'text/css';
    flipButtonStyleSheet.innerText = `
      .flip-button-white-oriented {
        background: #a1a1a150 !important;
        border: 3px solid white !important;
        color: white !important;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      }
       .flip-button-white-oriented:hover {
        background: #a1a1a150 !important;
        border: 3px solid black !important;
        color: black !important;
      }
      .flip-button-black-oriented {
        background: #a1a1a150 !important;
        border: 3px solid black !important;
        color: black !important;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      }
      .flip-button-black-oriented:hover {
        background: #a1a1a150 !important;
        border: 3px solid white !important;
        color: white !important;
      }
    `;
    document.head.appendChild(flipButtonStyleSheet);

    // Cleanup function to remove the style sheet when the component unmounts
    return () => {
      document.head.removeChild(flipButtonStyleSheet);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Use the global context instead of local state
  const {
    showEmoji,
    setShowEmoji,
    showStockfishIcons,
    setShowStockfishIcons,
    showArrows,
    setShowArrows,
    evaluation,
    showStockfish,
    setShowStockfish,
  } = useContext(ChessSocialContext);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const viewportWidth = window.innerWidth;
        const availableWidth = viewportWidth - 875;

        // Now container and board will be same size
        const containerSize = Math.min(availableWidth, 900);
        const boardSize = Math.max(containerSize, 500);

        setBoardSize(boardSize);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { setActiveTab, activeTab } = useContext(ChessSocialContext);

  // Add new state variables to track analysis data
  const [game, setGame] = useState(new Chess());

  // Move these dispatch calls into a useEffect to prevent infinite loops
  useEffect(() => {
    // Initial setup - run only once on component mount
    dispatch({ type: 'SET_MOVES', payload: [] });
    dispatch({ type: 'SET_STARRED_MOVES', payload: [] });
    dispatch({ type: 'SET_ORIENTATION', payload: 'white' });
  }, [dispatch]);

  // Add setContextFEN function to update state.currentFEN (missing in original)
  const setContextFEN = (fen) => dispatch({ type: 'SET_FEN', payload: fen });

  const [gameMetadata, setGameMetadata] = useState({
    visibility: 'Public',
    isPrivate: false,
  });

  // Track if we should use cached analysis for the current position
  const [hasAnalysisCache, setHasAnalysisCache] = useState(false);
  const [currentAnalysisCache, setCurrentAnalysisCache] = useState(null);

  const [step, setStep] = useState(0);
  // State to control the visibility of the controls section
  const [showControls, setShowControls] = useState(false);
  // Add state to track if annotation textarea is focused
  const [isAnnotationFocused, setIsAnnotationFocused] = useState(false);
  const [justUpdatedVariationContent, setJustUpdatedVariationContent] =
    useState(false);
  // Previous moves count for comparing when moves have changed
  const prevMovesCountRef = useRef(0);

  // Add an effect to log when annotation focus changes
  useEffect(() => {}, [isAnnotationFocused]);

  // Add debugging for document-level events to help identify any issues
  useEffect(() => {
    const documentKeyListener = (e) => {
      if (
        isAnnotationFocused &&
        ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)
      ) {
      }
    };

    document.addEventListener('keydown', documentKeyListener);
    return () => {
      document.removeEventListener('keydown', documentKeyListener);
    };
  }, [isAnnotationFocused]);

  // Effect to close the control panel when a new move is made
  useEffect(() => {
    // Skip the initial render
    if (moves.length > 0 && prevMovesCountRef.current !== moves.length) {
      // Close the control panel when a move is made
      setShowControls(false);
    }
    // Update the previous moves count
    prevMovesCountRef.current = moves.length;
  }, [moves]);

  const [gameCaption, setGameCaption] = useState('');
  const [moveContent, setMoveContent] = useState('');
  const [variationContent, setVariationContent] = useState('');
  const [tags, setTags] = useState([]);
  // Add state for tag selection error indication
  const [showTagError, setShowTagError] = useState(false);

  // New state variables for opening selection
  const [isOpeningSelectionMode, setIsOpeningSelectionMode] = useState(false);
  const [openingOptions, setOpeningOptions] = useState([]);
  const [isLoadingOpenings, setIsLoadingOpenings] = useState(false);
  const [selectedOpening, setSelectedOpening] = useState(null);

  const [rootFen, setRootFen] = useState(CHESSBOARD.START_POSITION);
  const [pgn, setPgn] = useState('');
  // These dispatch calls have been moved to the useEffect above
  // dispatch({ type: 'SET_STARRED_MOVES', payload: [] });
  // dispatch({ type: 'SET_ORIENTATION', payload: 'black' });

  const [boardStartingTurn, setBoardStartingTurn] = useState('w'); // Add state for tracking the FEN's starting turn

  const { screams, loading } = props.data;

  // Near the top of the component where the useMoveIndex hook is used:
  const [skipMoveIndexUpdate, setSkipMoveIndexUpdate] = useState(false);

  const [currentMoveIdx, setCurrentMoveIdx] = useMoveIndex({
    moves,
    gameMetadata,
    setGame,
    setMoveContent,
    setVariationContent,
    skipUpdate: isAnnotationFocused || skipMoveIndexUpdate,
  });

  // Add effect to sync variationContent with current move's variation caption
  useEffect(() => {
    // Skip updates when annotation is focused, move updates are blocked, or we just manually updated
    if (
      isAnnotationFocused ||
      skipMoveIndexUpdate ||
      justUpdatedVariationContent
    ) {
      // console.log('[2AddGame] Skipping variationContent sync - annotation focused, move update blocked, or just updated manually');
      return;
    }

    if (isMoveInVariation(state.currentMove, moves)) {
      // ENHANCED: Handle start position variations
      if (state.currentMove.startsWith('-1|')) {
        // This is a start position variation
        const parts = state.currentMove.split('|');
        if (parts.length >= 2) {
          const variationPart = parts[1];
          const [varIdx] = variationPart.split(':').map(Number);

          const caption =
            state?.startPosition?.variationCaptions?.[varIdx] || '';
          // Only update if the caption is different from current content
          if (caption !== variationContent) {
            // console.log(
            //   '[2AddGame] Updating start position variation caption:',
            //   {
            //     currentMove: state.currentMove,
            //     varIdx,
            //     caption,
            //     currentVariationContent: variationContent,
            //   },
            // );
            setVariationContent(caption);
          } else {
            // console.log(
            //   '[2AddGame] Start position variation caption unchanged:',
            //   {
            //     currentMove: state.currentMove,
            //     varIdx,
            //     caption,
            //     currentVariationContent: variationContent,
            //   },
            // );
          }
        }
      } else {
        // Regular variation handling
        const [mainMove, variationIdx] = getMoveAndVariationIndices(
          state.currentMove,
          moves,
        );

        // Check if the move has variation captions
        if (mainMove && typeof variationIdx === 'number') {
          const caption = mainMove.variationCaptions?.[variationIdx] || '';
          // Only update if the caption is different from current content
          // This prevents overwriting during typing sessions
          if (caption !== variationContent) {
            setVariationContent(caption);
          } else {
          }
        } else {
          // Only clear if we currently have content and we're sure there's no saved caption
          if (
            variationContent !== '' &&
            !mainMove?.variationCaptions?.[variationIdx]
          ) {
            setVariationContent('');
          }
        }
      }
    } else {
      // Not in a variation - only clear if we're confident there's no saved content anywhere
      // and we're not in the middle of editing
      if (
        variationContent !== '' &&
        !isAnnotationFocused &&
        !justUpdatedVariationContent
      ) {
        setVariationContent('');
      }
    }
  }, [
    state.currentMove,
    moves,
    isAnnotationFocused,
    skipMoveIndexUpdate,
    justUpdatedVariationContent,
    state.startPosition?.variationCaptions,
  ]);

  // Helper function to compare FENs ignoring move counters
  const fenPositionsEqual = (fen1, fen2) => {
    if (!fen1 || !fen2) return false;

    // Compare the first 4 parts of the FEN (piece positions, side to move, castling rights, en passant)
    // Ignore the halfmove and fullmove counters which don't affect the board state
    const fen1Base = fen1.split(' ').slice(0, 4).join(' ');
    const fen2Base = fen2.split(' ').slice(0, 4).join(' ');

    return fen1Base === fen2Base;
  };

  // Cache optimization tracking for debugging
  const logCacheOptimization = (type, fen, moveId, cached) => {
    const timestamp = new Date().toLocaleTimeString();
    // console.log(`🎯 [${timestamp}] CACHE OPTIMIZATION: ${type}`, {
    //   fen: fen?.substring(0, 20) + '...',
    //   moveId,
    //   cached: cached ? '✅ CACHE HIT' : '❌ CACHE MISS',
    //   totalMovesInCache: state.moves?.filter(m => m.analysis)?.length || 0
    // });

    // Log to window for debugging
    if (!window.cacheOptimizationLog) window.cacheOptimizationLog = [];
    window.cacheOptimizationLog.push({
      timestamp,
      type,
      fen,
      moveId,
      cached,
      totalMovesInCache: state.moves?.filter((m) => m.analysis)?.length || 0,
    });

    // Keep only last 50 entries
    if (window.cacheOptimizationLog.length > 50) {
      window.cacheOptimizationLog = window.cacheOptimizationLog.slice(-50);
    }
  };

  // Create a function to save analysis data for the current move
  const saveAnalysisToMove = (analysisData) => {
    if (!analysisData) return;

    // Store the current position FEN before any state updates
    const currentFEN = window.currentFEN;
    const currentMoveId = window.getCurrentMoveIndex
      ? window.getCurrentMoveIndex()
      : state.currentMove;

    if (currentFEN) {
      // Store the position in context for possible recovery
      dispatch({ type: 'STORE_POSITION', payload: currentFEN });
      //
    }

    // First, try to find the move by FEN if available
    const analysisFEN = analysisData.fen || analysisData.positionFEN;
    let moveIndex = -1;
    let moveId = currentMoveId || state.currentMove;
    let foundExactMatch = false;
    let isStartPosition = false;

    // CRITICAL: Validate that the analysis FEN matches the current position
    // ENHANCED: Add special handling for starting position variations
    const isStartingPositionVariation = moveId.startsWith('-1|');

    if (
      analysisFEN &&
      currentFEN &&
      !fenPositionsEqual(analysisFEN, currentFEN)
    ) {
      // For starting position variations, be more lenient with FEN validation
      if (isStartingPositionVariation) {
        // console.log(
        //   '2AddGame: FEN mismatch detected for starting position variation, but proceeding with analysis',
        // );
        // console.log('2AddGame: Analysis FEN:', analysisFEN);
        // console.log('2AddGame: Current FEN:', currentFEN);
        // console.log('2AddGame: Move ID:', moveId);

        // Allow the analysis to proceed for starting position variations
        // as the FEN mismatch might be expected during navigation
      } else {
        console.warn(
          '2AddGame: RACE CONDITION DETECTED - Analysis FEN does not match current position',
        );
        console.warn('2AddGame: Analysis FEN:', analysisFEN);
        console.warn('2AddGame: Current FEN:', currentFEN);
        console.warn(
          '2AddGame: Discarding outdated analysis to prevent contamination',
        );

        // CRITICAL FIX: Be smarter about when to trigger fresh analysis
        // Check if the current position already has analysis or if user just navigated
        const currentMoveObj = getMoveObject(state.currentMove);
        const currentPositionHasAnalysis = currentMoveObj?.analysis;

        if (!currentPositionHasAnalysis) {
          if (
            window.updatePositionForAnalysis &&
            typeof window.updatePositionForAnalysis === 'function'
          ) {
            // Force new analysis with the current FEN
            window.updatePositionForAnalysis(currentFEN);
          } else {
            // Fallback: dispatch analysis request event
            window.dispatchEvent(
              new CustomEvent('request-stockfish-analysis', {
                detail: { fen: currentFEN, forceCalculation: true },
              }),
            );
          }
        } else {
        }

        return; // Discard this analysis to prevent contamination
      }
    }

    // Check if this is the start position
    if (moveId === '-1' && analysisFEN) {
      // For start position, compare with the current startPosition FEN or rootFen
      const startPositionFEN = state.startPosition?.fen || rootFen;
      if (fenPositionsEqual(analysisFEN, startPositionFEN)) {
        isStartPosition = true;
        //
      }
    }

    // If we have a FEN, try to find the exact move it belongs to
    if (analysisFEN && !isStartPosition) {
      // Try to find the move that matches this position
      state.moves.forEach((move, idx) => {
        // Check if this move has a matching FEN - using our helper function to compare positions
        if (move.fen && fenPositionsEqual(move.fen, analysisFEN)) {
          moveId = idx.toString();
          moveIndex = idx;
          foundExactMatch = true;
          //
        }
      });

      // If no exact match found and we're at the starting position,
      // check if this analysis is for one of the moves
      if (!foundExactMatch && moveId === '-1') {
        //
      }
    }

    // If no FEN match but we have a current move selected, use that
    if (!foundExactMatch && !isStartPosition && moveId !== '-1') {
      moveIndex = parseInt(moveId);
      //
    }

    // Handle start position analysis separately
    if (isStartPosition) {
      //
      //
      //
      //

      const startAnalysisWithFEN = {
        // Ensure we have both pvs and lines formats for compatibility
        pvs:
          analysisData.pvs ||
          (analysisData.lines
            ? analysisData.lines.map((line) => ({
                bestMove: line.move || '',
                evaluation: line.score || '0.0',
                moves: line.pv || [],
              }))
            : []),

        lines:
          analysisData.lines ||
          (analysisData.pvs
            ? analysisData.pvs.map((pv, index) => ({
                move: pv.bestMove || pv.move || '',
                score: analysisData.scores?.[index] || pv.evaluation || '0.0',
                pv: pv.moves || [],
              }))
            : []),

        scores:
          analysisData.scores ||
          (analysisData.lines
            ? analysisData.lines.map((line) => line.score || '0.0')
            : []),

        classification: analysisData.classification || null,
        depth: analysisData.depth || null,
        positionFEN: analysisFEN,
        timestamp: Date.now(),
        isStartPosition: true,
      };

      //

      // Save start position analysis to new startPosition structure
      dispatch({
        type: 'UPDATE_START_POSITION_ANALYSIS',
        payload: startAnalysisWithFEN,
      });

      // Update the window cache
      window.currentAnalysisCache = startAnalysisWithFEN;

      // Update classification data if available
      if (startAnalysisWithFEN.classification) {
        window.moveClassificationData = {
          classification: startAnalysisWithFEN.classification,
          positionFEN: startAnalysisWithFEN.positionFEN,
          timestamp: Date.now(),
        };
      }

      // Dispatch event to notify UI components that analysis is ready
      window.dispatchEvent(
        new CustomEvent('stockfish-analysis-ready', {
          detail: {
            analysis: startAnalysisWithFEN,
            position: 'start',
            fen: analysisFEN,
          },
        }),
      );

      //
      return;
    }

    // If we still don't have a valid move ID and it's not the start position or starting position variation, we can't save the analysis
    if (
      (moveId === '-1' || moveIndex < 0) &&
      !isStartPosition &&
      !isStartingPositionVariation
    ) {
      console.warn(
        '2AddGame: Cannot save analysis - no valid move found for:',
        {
          analysisFEN,
          currentMoveId: state.currentMove,
          isStartPosition,
          isStartingPositionVariation,
        },
      );
      return;
    }

    // For starting position variations, we need to save the analysis to the appropriate variation move
    if (isStartingPositionVariation) {
      // console.log(
      //   `✅ 2AddGame: Saving analysis to starting position variation ${moveId}`,
      // );

      // Parse the starting position variation move ID
      const match = moveId.match(/^-1\|(\d+):(\d+)$/);
      if (!match) {
        console.warn(
          `❌ 2AddGame: Invalid starting position variation format: ${moveId}`,
        );
        return;
      }

      const varIdx = parseInt(match[1], 10);
      const moveIdx = parseInt(match[2], 10);

      // Use the dispatch system to update the starting position variation move
      if (dispatch) {
        const analysisWithFEN = {
          // Ensure we have both pvs and lines formats for compatibility
          pvs:
            analysisData.pvs ||
            (analysisData.lines
              ? analysisData.lines.map((line) => ({
                  bestMove: line.move || '',
                  evaluation: line.score || '0.0',
                  moves: line.pv || [],
                }))
              : []),
          lines:
            analysisData.lines ||
            (analysisData.pvs
              ? analysisData.pvs.map((pv) => ({
                  move: pv.bestMove || '',
                  score: pv.evaluation || '0.0',
                  pv: pv.moves || [],
                }))
              : []),
          depth: analysisData.depth || 0,
          positionFEN: analysisFEN,
          timestamp: Date.now(),
          scores:
            analysisData.scores ||
            (analysisData.pvs
              ? analysisData.pvs.map((pv) => pv.evaluation || '0.0')
              : []),
          isComplete: true,
        };

        // Update the starting position variation move with analysis
        dispatch({
          type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS',
          payload: {
            moveId,
            properties: { analysis: analysisWithFEN },
          },
        });

        // Also update the global cache for immediate UI updates
        window.currentAnalysisCache = analysisWithFEN;

        // Dispatch event to notify UI components
        window.dispatchEvent(
          new CustomEvent('stockfish-analysis-ready', {
            detail: {
              analysis: analysisWithFEN,
              position: 'starting-position-variation',
              moveId: moveId,
              fen: analysisFEN,
            },
          }),
        );
      }

      return; // Exit early for starting position variations
    }

    // console.log(`2AddGame: Saving analysis to move ${moveId} (index ${moveIndex})`, {
    //   moveId,
    //   moveIndex,
    //   analysisDepth: analysisData.depth,
    //   analysisFEN: analysisFEN,
    //   hasLines: !!analysisData.lines?.length,
    //   hasPvs: !!analysisData.pvs?.length
    // });

    // PHASE 4: Standardize analysis data formats with consistent pvs/lines conversion
    const analysisWithFEN = {
      // Ensure we have both pvs and lines formats for compatibility
      pvs:
        analysisData.pvs ||
        (analysisData.lines
          ? analysisData.lines.map((line) => ({
              bestMove: line.move || '',
              evaluation: line.score || '0.0',
              moves: line.pv || [],
            }))
          : []),

      lines:
        analysisData.lines ||
        (analysisData.pvs
          ? analysisData.pvs.map((pv, index) => ({
              move: pv.bestMove || pv.move || '',
              score: analysisData.scores?.[index] || pv.evaluation || '0.0',
              pv: pv.moves || [],
            }))
          : []),

      scores:
        analysisData.scores ||
        (analysisData.lines
          ? analysisData.lines.map((line) => line.score || '0.0')
          : []),

      classification: analysisData.classification || null,
      depth: analysisData.depth || null,
      positionFEN: analysisFEN || currentFEN, // Ensure we always store the position FEN
      timestamp: Date.now(), // Add timestamp for cache validation
    };

    // Use the new UPDATE_MOVE_ANALYSIS action
    dispatch({
      type: 'UPDATE_MOVE_ANALYSIS',
      payload: {
        moveId: moveId,
        analysisData: analysisWithFEN,
      },
    });

    // Update the window cache with this analysis data
    window.currentAnalysisCache = {
      ...analysisData,
      positionFEN: analysisFEN || currentFEN,
    };

    // Dispatch event to notify UI components that analysis is ready
    window.dispatchEvent(
      new CustomEvent('stockfish-analysis-ready', {
        detail: {
          analysis: analysisWithFEN,
          position: 'move',
          moveId: moveId,
          fen: analysisFEN || currentFEN,
        },
      }),
    );

    //   console.log('2AddGame: Updated move with analysis data:', {
    //     moveIndex,
    //   moveId,
    //   depth: analysisData.depth,
    //   lines: analysisData.lines?.length || analysisData.pvs?.length || 0,
    //   fen: analysisFEN || currentFEN
    // });

    // Clear the skipPositionUpdate flag after a short delay
    // This allows the update to complete before re-enabling position updates
    setTimeout(() => {
      dispatch({ type: 'CLEAR_SKIP_POSITION_UPDATE' });
      //
    }, 300);
  };

  // Enhanced function to search for cached analysis by FEN across all moves
  const getCachedAnalysisForFEN = (fen) => {
    if (!fen || !state.moves || state.moves.length === 0) {
      return null;
    }

    // Search through all moves to find one with matching FEN and analysis
    for (let i = 0; i < state.moves.length; i++) {
      const move = state.moves[i];
      if (
        move &&
        move.fen &&
        move.analysis &&
        fenPositionsEqual(move.fen, fen)
      ) {
        //
        logCacheOptimization(
          'getCachedAnalysisForFEN',
          fen,
          i.toString(),
          true,
        );

        // Format the analysis data for compatibility
        const formattedAnalysis = move.analysis.pvs
          ? {
              ...move.analysis,
              lines: move.analysis.pvs.map((pv, index) => ({
                move: pv.bestMove || pv.move || '',
                score: move.analysis.scores?.[index] || pv.evaluation || '0.0',
                pv: pv.moves || [],
              })),
              positionFEN: move.fen,
              moveId: i.toString(),
            }
          : {
              ...move.analysis,
              positionFEN: move.fen,
              moveId: i.toString(),
            };

        return formattedAnalysis;
      }
    }

    //
    logCacheOptimization('getCachedAnalysisForFEN', fen, 'none', false);
    return null;
  };

  // Enhanced function to check for cached analysis by move index or FEN with strict validation
  const checkForCachedAnalysis = (moveIndex, targetFEN = null) => {
    try {
      //

      // Handle start position analysis (index -1) with new startPosition support
      if (moveIndex === '-1') {
        //

        // Check if we have startPosition with analysis in the game state
        if (state.startPosition?.analysis?.pvs?.length > 0) {
          const startAnalysis = state.startPosition.analysis;
          //

          // Validate the FEN matches if provided
          if (targetFEN) {
            const cachedFEN = state.startPosition.fen;
            const targetFENBase = targetFEN.split(' ').slice(0, 4).join(' ');
            const cachedFENBase = cachedFEN.split(' ').slice(0, 4).join(' ');

            if (targetFENBase !== cachedFENBase) {
              //
              //

              // Clear the old analysis since the position changed
              dispatch({
                type: 'UPDATE_START_POSITION_ANALYSIS',
                payload: { pvs: [], scores: [], classification: '', depth: 0 },
              });

              return false;
            }
          }

          // Set global cache to match startPosition analysis
          window.currentAnalysisCache = startAnalysis;

          // Update classification data if available
          if (startAnalysis.classification) {
            window.moveClassificationData = {
              classification: startAnalysis.classification,
              positionFEN: state.startPosition.fen,
              timestamp: Date.now(),
            };
          }

          //
          return true;
        }

        // Fallback: Check if we have any analysis cached globally for start position
        if (
          window.currentAnalysisCache &&
          window.currentAnalysisCache.lines?.length > 0
        ) {
          //
          return true;
        }

        //
        return false;
      }

      // Handle regular move analysis (existing logic)
      if (!state.moves || moveIndex < 0 || moveIndex >= state.moves.length) {
        //
        return false;
      }

      const move = state.moves[moveIndex];
      if (!move) {
        //
        return false;
      }

      // Check if move has analysis data
      if (move && move.analysis) {
        // CRITICAL: If we have a targetFEN, validate that the move's FEN matches
        if (targetFEN && move.fen && !fenPositionsEqual(move.fen, targetFEN)) {
          //
          logCacheOptimization(
            'checkForCachedAnalysis-moveIndex',
            targetFEN,
            moveIndex,
            false,
          );
          setHasAnalysisCache(false);
          setCurrentAnalysisCache(null);
          return false;
        }

        // Format the analysis data to ensure it has the right structure
        const formattedAnalysis = move.analysis.pvs
          ? {
              ...move.analysis,
              // If we have pvs but no lines, create lines from pvs for compatibility
              lines: move.analysis.pvs.map((pv, index) => ({
                move: pv.bestMove || pv.move || '',
                score: move.analysis.scores?.[index] || pv.evaluation || '0.0',
                pv: pv.moves || [],
              })),
              positionFEN: move.fen,
              moveId: moveIndex,
            }
          : {
              ...move.analysis,
              positionFEN: move.fen,
              moveId: moveIndex,
            };

        //
        logCacheOptimization(
          'checkForCachedAnalysis-moveIndex',
          targetFEN || move.fen,
          moveIndex,
          true,
        );

        // We have cached analysis for this move
        setHasAnalysisCache(true);
        setCurrentAnalysisCache(formattedAnalysis);

        // Update global window objects for Stockfish.js to access
        window.currentAnalysisCache = formattedAnalysis;
        window.moveClassificationData = {
          classification: move.analysis.classification || null,
          positionFEN: move.fen || null,
          timestamp: Date.now(),
        };

        return true;
      } else {
        //
        logCacheOptimization(
          'checkForCachedAnalysis-moveIndex',
          targetFEN,
          moveIndex,
          false,
        );

        // No cached analysis found
        setHasAnalysisCache(false);
        setCurrentAnalysisCache(null);
        return false;
      }

      // ... rest of existing logic ...
    } catch (error) {
      // console.error('Error checking for cached analysis:', error);
      return false;
    }
  };

  // Create a function to receive analysis data from Stockfish.js using useCallback
  const receiveStockfishAnalysis = useCallback(
    (analysisData) => {
      // Only save complete analysis data that has reached target depth
      if (analysisData && analysisData.isComplete) {
        //

        // Store the current FEN for reference before any updates
        const currentFenBeforeUpdate = window.currentFEN;

        // Save the current variation content
        const currentVariationContent = variationContent;

        // PHASE 4: Format analysis data using standardized format function
        const formattedAnalysisData = {
          ...analysisData,
          // Ensure we have both pvs and lines formats for compatibility
          pvs:
            analysisData.pvs ||
            (analysisData.lines
              ? analysisData.lines.map((line) => ({
                  bestMove: line.move || '',
                  evaluation: line.score || '0.0',
                  moves: line.pv || [],
                }))
              : []),

          lines:
            analysisData.lines ||
            (analysisData.pvs
              ? analysisData.pvs.map((pv, index) => ({
                  move: pv.bestMove || pv.move || '',
                  score: analysisData.scores?.[index] || pv.evaluation || '0.0',
                  pv: pv.moves || [],
                }))
              : []),

          // Make sure scores exist
          scores:
            analysisData.scores ||
            (analysisData.lines
              ? analysisData.lines.map((line) => line.score || '0.0')
              : []),

          // Ensure the FEN is stored with the analysis data
          fen:
            analysisData.fen ||
            analysisData.positionFEN ||
            currentFenBeforeUpdate,
          positionFEN:
            analysisData.fen ||
            analysisData.positionFEN ||
            currentFenBeforeUpdate,
          timestamp: Date.now(), // Add timestamp for cache validation
        };

        // Get the moveId from the analysis data or try to find by FEN
        const associatedMoveId = analysisData.moveId || null;

        //

        // Log the analysis lines for debugging
        // console.log('2AddGame: Analysis results contain lines:',
        //   formattedAnalysisData.pvs?.length || 0,
        //   'with scores:',
        //   formattedAnalysisData.scores || []
        // );

        // Save the analysis data to the appropriate move
        saveAnalysisToMove(formattedAnalysisData);

        // Restore the variation content after the update
        setVariationContent(currentVariationContent);

        // Also update the cache for immediate display in UI
        if (formattedAnalysisData.fen) {
          // Use the associated moveId if available, otherwise use current move
          const moveIndex = associatedMoveId || state.currentMove;

          // Only update the cache if we're currently viewing the analyzed position
          // or if we're viewing the starting position and the analysis has a matching moveId
          if (
            moveIndex !== '-1' ||
            (associatedMoveId && state.currentMove === '-1')
          ) {
            //
            setHasAnalysisCache(true);
            setCurrentAnalysisCache(formattedAnalysisData);

            // Update global cache variable for Stockfish.js to access
            window.currentAnalysisCache = formattedAnalysisData;
          }
        }

        // Make sure the board position wasn't reset by the analysis update
        if (
          analysisData.isAnalysisOnly &&
          window.currentFEN !== currentFenBeforeUpdate &&
          currentFenBeforeUpdate
        ) {
          console.warn(
            '2AddGame: Board position was reset during analysis. Restoring position.',
          );

          // Use the board ref to directly update the position if available
          if (boardRef.current && boardRef.current.updateBoardFEN) {
            //
            boardRef.current.updateBoardFEN(currentFenBeforeUpdate);
          } else {
            // Fallback to window global
            window.currentFEN = currentFenBeforeUpdate;

            // Dispatch a custom event to notify the board of the position change
            window.dispatchEvent(
              new CustomEvent('force-board-position', {
                detail: { fen: currentFenBeforeUpdate },
              }),
            );
          }
        }
      }
    },
    [variationContent, boardRef, state.currentMove],
  );

  // Enhanced function to handle Stockfish analysis requests with comprehensive cache checking
  const handleStockfishAnalysisRequest = useCallback(
    (fen, moveIdOverride = null) => {
      //

      // Store the current FEN to ensure it doesn't get lost during analysis
      if (fen) {
        window.currentFEN = fen;
      }

      // First update the position in ChessAnalysisContext
      if (updatePositionSilently) {
        updatePositionSilently(fen);
      }

      // PHASE 1: Check for cached analysis by FEN first (most comprehensive)
      const cachedAnalysisByFEN = getCachedAnalysisForFEN(fen);
      if (cachedAnalysisByFEN) {
        //
        logCacheOptimization(
          'handleStockfishAnalysisRequest',
          fen,
          cachedAnalysisByFEN.moveId,
          true,
        );
        setHasAnalysisCache(true);
        setCurrentAnalysisCache(cachedAnalysisByFEN);
        window.currentAnalysisCache = cachedAnalysisByFEN;
        return; // Early return - no need to run Stockfish
      }

      // PHASE 2: Check by move index if no FEN match found
      const currentMoveId = moveIdOverride || state.currentMove;
      let moveIdToUse = currentMoveId;
      let fenAssociatedWithMove = false;

      if (currentMoveId === '-1' && state.moves.length > 0) {
        // Try to find the move with this exact FEN
        state.moves.forEach((move, idx) => {
          if (move.fen && fenPositionsEqual(move.fen, fen)) {
            moveIdToUse = idx.toString();
            fenAssociatedWithMove = true;
            //
          }
        });
      }

      // Check if we have cached analysis for this position by move index
      const hasCachedAnalysis = checkForCachedAnalysis(moveIdToUse, fen);

      if (!hasCachedAnalysis) {
        //
        logCacheOptimization(
          'handleStockfishAnalysisRequest',
          fen,
          moveIdToUse,
          false,
        );

        // Clear any previous analysis cache to ensure we're not using stale data
        window.currentAnalysisCache = null;

        // Mark the analysis as in progress
        setHasAnalysisCache(false);
        setCurrentAnalysisCache(null);

        // If no cached analysis, trigger a Stockfish analysis request
        // by dispatching an event that Stockfish.js listens for
        window.dispatchEvent(
          new CustomEvent('stockfish-analyze-position', {
            detail: {
              fen,
              moveId: moveIdToUse,
              isAnalysisOnly: true, // Mark this as analysis-only to prevent board resets
              timestamp: Date.now(),
              fenAssociatedWithMove,
              forceCalculation: true, // Always force a fresh calculation
            },
          }),
        );

        //
      } else {
        //
      }
    },
    [
      state.currentMove,
      state.moves,
      updatePositionSilently,
      checkForCachedAnalysis,
      getCachedAnalysisForFEN,
    ],
  );

  // Add an effect to expose the analysis functions to the window
  useEffect(() => {
    // NOTE: Commented out to allow useUnifiedStockfishAnalysis to handle callbacks
    // window.receiveStockfishAnalysis = receiveStockfishAnalysis;

    // Also expose the currentAnalysisCache for Stockfish.js to access
    window.currentAnalysisCache = currentAnalysisCache;

    // PHASE 1: Expose enhanced cache functions globally
    window.getCachedAnalysisForFEN = getCachedAnalysisForFEN;
    window.checkForCachedAnalysis = checkForCachedAnalysis;

    // Expose cache debugging function
    window.debugCacheOptimization = () => {
      const totalMoves = state.moves?.length || 0;
      const movesWithAnalysis =
        state.moves?.filter((m) => m.analysis)?.length || 0;
      const cacheLog = window.cacheOptimizationLog || [];
      const cacheHits = cacheLog.filter((entry) => entry.cached).length;
      const cacheMisses = cacheLog.filter((entry) => !entry.cached).length;
      const hitRate =
        cacheLog.length > 0
          ? ((cacheHits / cacheLog.length) * 100).toFixed(1)
          : 0;

      return {
        totalMoves,
        movesWithAnalysis,
        cacheHits,
        cacheMisses,
        hitRate: hitRate + '%',
        recentLog: cacheLog.slice(-10),
      };
    };

    // Expose a function for 3Board.jsx to trigger analysis
    window.updatePositionForAnalysis = (fen) => {
      // Skip analysis for starting position variations to prevent conflicts
      if (state.currentMove && state.currentMove.startsWith('-1|')) {
        return;
      }
      handleStockfishAnalysisRequest(fen);
    };

    // Expose a function to get the current move index
    window.getCurrentMoveIndex = () => state.currentMove;

    // Expose the moves array and getMove function for Stockfish.js
    window.moves = state.moves;
    window.getMove = (moves, moveId) => {
      if (moveId === '-1' || !moves || !Array.isArray(moves)) return null;
      return moves[parseInt(moveId)];
    };

    // Add debugging functions for opening data
    window.checkOpeningData = (moveId) => {
      const moves = window.moves || state.moves;
      const move = getMove(moves, moveId || state.currentMove);
      //
      //
      //
      return move?.opening;
    };

    window.debugAllOpenings = () => {
      const moves = window.moves || state.moves;
      //
      //

      let foundOpenings = 0;
      moves.forEach((move, index) => {
        if (move.opening) {
          foundOpenings++;
          //
        } else {
          //
        }
      });
      //
      return { totalMoves: moves.length, movesWithOpenings: foundOpenings };
    };

    // Test function to manually test opening data saving
    window.testOpeningSave = (
      moveId = null,
      testOpening = { name: 'Test Opening', eco: 'A00' },
    ) => {
      const targetMoveId = moveId || state.currentMove;
      //

      // Check if the move exists first
      const moves = window.moves || state.moves;
      const targetMove = getMove(moves, targetMoveId);
      if (!targetMove) {
        console.error('❌ Target move does not exist:', targetMoveId);
        return false;
      }

      //

      dispatch({
        type: 'UPDATE_MOVE_PROPERTIES',
        payload: {
          moveId: targetMoveId,
          properties: { opening: testOpening },
        },
      });

      setTimeout(() => {
        const result = window.checkOpeningData(targetMoveId);
        //
        if (result) {
          //
        } else {
          // console.error('❌ Test failed - opening data not saved');
        }
        return result;
      }, 100);
    };

    // Add a function to monitor opening saves in real-time
    window.monitorOpeningSaves = () => {
      //

      // Override the dispatch function temporarily to log opening updates
      const originalDispatch = dispatch;
      window.originalDispatch = originalDispatch;

      window.addEventListener('beforeunload', () => {
        // Restore original dispatch when page unloads
        if (window.originalDispatch) {
          dispatch = window.originalDispatch;
        }
      });

      return 'Monitoring active. Opening save attempts will be logged.';
    };

    // Function to debug FEN to move mapping
    window.debugFenToMove = (testFen = null) => {
      const fen = testFen || currentFen;
      if (!fen) {
        //
        return;
      }

      //

      // Check if it's the starting position
      if (fenPositionsEqual(fen, rootFen)) {
        //
        return { moveId: '-1', moveIndex: -1, isStartPosition: true };
      }

      // Try to find which move results in this FEN
      try {
        const tempGame = new Chess(rootFen);
        const moves = window.moves || state.moves;

        for (let i = 0; i < moves.length; i++) {
          const move = moves[i];
          if (move && move.notation) {
            try {
              tempGame.move(move.notation);

              if (fenPositionsEqual(tempGame.fen(), fen)) {
                //
                //
                if (move.opening) {
                  //
                }
                return { moveId: i.toString(), moveIndex: i, move: move };
              }
            } catch (moveError) {
              console.error(
                `❌ Error applying move ${i}: ${move.notation}`,
                moveError,
              );
              break;
            }
          }
        }

        return { moveId: null, moveIndex: -1, found: false };
      } catch (error) {
        console.error('❌ Error in FEN to move mapping:', error);
        return { error: error.message };
      }
    };

    // Event listener for Stockfish analysis requests
    const analysisRequestHandler = (event) => {
      if (event.detail && event.detail.fen) {
        handleStockfishAnalysisRequest(event.detail.fen);
      }
    };

    // Event listener for analysis start - to preserve position
    const analysisStartingHandler = (event) => {
      if (event.detail && event.detail.fen && event.detail.preservePosition) {
        // Store the current position FEN for possible recovery
        if (event.detail.fen) {
          // Store the position in context for possible recovery
          dispatch({ type: 'STORE_POSITION', payload: event.detail.fen });
          window.currentFEN = event.detail.fen;
        }
      }
    };

    // Add event listeners
    window.addEventListener(
      'request-stockfish-analysis',
      analysisRequestHandler,
    );
    window.addEventListener(
      'stockfish-analysis-starting',
      analysisStartingHandler,
    );

    // Clean up when component unmounts
    return () => {
      // NOTE: Commented out to allow useUnifiedStockfishAnalysis to handle callbacks
      // window.receiveStockfishAnalysis = undefined;
      window.currentAnalysisCache = undefined;
      window.getCachedAnalysisForFEN = undefined;
      window.checkForCachedAnalysis = undefined;
      window.debugCacheOptimization = undefined;
      window.updatePositionForAnalysis = undefined;
      window.getCurrentMoveIndex = undefined;
      window.moves = undefined;
      window.getMove = undefined;
      window.removeEventListener(
        'request-stockfish-analysis',
        analysisRequestHandler,
      );
      window.removeEventListener(
        'stockfish-analysis-starting',
        analysisStartingHandler,
      );
    };
  }, [
    receiveStockfishAnalysis,
    currentAnalysisCache,
    handleStockfishAnalysisRequest,
    state.moves,
    state.currentMove,
    getCachedAnalysisForFEN,
    checkForCachedAnalysis,
  ]);

  // Enhanced moveNavigation with comprehensive cache synchronization
  const moveNavigation = (targetIndex, shouldUpdateContent = true) => {
    // Get the current position - don't apply it yet, let the Board component handle it
    // by using currentMoveIdx which was already updated in handleMoveClick

    // Handle variations properly by setting direct flag for instant rendering
    const isVariationMove = targetIndex.includes('|');
    if (isVariationMove) {
      try {
        // Set global flag for direct variation transition if not already set
        // This helps ensure the position renders immediately
        if (
          !window.chessDirectVariationMove ||
          window.chessDirectVariationMove.moveNotation !== targetIndex
        ) {
          window.chessDirectVariationMove = {
            moveNotation: targetIndex,
            timestamp: Date.now(),
          };
        }
      } catch (e) {
        console.error('Error setting variation flags:', e);
      }
    }

    // PHASE 3: Update cache during move navigation
    // Get the FEN for the target position to check cache
    let targetFEN = null;
    try {
      if (targetIndex === '-1') {
        targetFEN = rootFen;
      } else {
        // Calculate the FEN for this position
        const chessInstance = new Chess(rootFen);
        const targetIdx = parseInt(targetIndex);

        // Apply moves up to the target index
        for (let i = 0; i <= targetIdx; i++) {
          if (i < moves.length) {
            try {
              const result = chessInstance.move(moves[i].notation);
              if (!result) {
                console.error(
                  `Navigation - Failed to apply move ${i}: ${moves[i].notation}`,
                );
                break;
              }
            } catch (err) {
              console.error(
                `Navigation - Error applying move ${i}: ${moves[i].notation}`,
                err,
              );
              break;
            }
          }
        }
        targetFEN = chessInstance.fen();
      }
    } catch (e) {
      console.error('Error calculating target FEN during navigation:', e);
    }

    // PHASE 3: Clear potentially stale cache before checking for new position

    setHasAnalysisCache(false);
    setCurrentAnalysisCache(null);

    // Check for cached analysis for the target position
    if (targetFEN) {
      checkForCachedAnalysis(targetIndex, targetFEN);
    } else {
      // Fallback to move index only

      checkForCachedAnalysis(targetIndex);
    }

    // Update content for current move if needed
    if (shouldUpdateContent && moveContent) {
      updateContentForMove(moveContent);
    }

    // Clear any temporary annotation state
    setIsAnnotationFocused(false);

    // NOTE: We don't need to call setCurrentMoveIdx here as it's already
    // been set in handleMoveClick before this is called
  };

  // Fix the currentMoveIdx state to prevent the original useMoveIndex effect from running
  const handleMoveClick = (idx, shouldUpdateContent = true) => {
    // Check if this would be a variation move
    const isVariationMove = idx.includes('|');

    // If this is a variation move, set the direct variation flag
    if (isVariationMove) {
      try {
        // Set global flag for direct variation transition
        window.chessDirectVariationMove = {
          moveNotation: idx,
          timestamp: Date.now(),
        };

        // Preload the position if possible
        if (window.preloadVariationPosition) {
          window.preloadVariationPosition(idx);
        }
      } catch (e) {
        console.error(
          '🔍 BOARDBUG [2AddGame.handleMoveClick] Error setting variation flags:',
          e,
        );
      }
    }

    // Log any movement in currentMoveIdx
    if (currentMoveIdx !== idx) {
      // CRITICAL FIX: Cancel any pending analysis when navigating away
      if (cancelStockfishAnalysis) {
        cancelStockfishAnalysis();
      }

      // Clear cache state before checking for new position
      setHasAnalysisCache(false);
      setCurrentAnalysisCache(null);

      // Clear global cache if it exists (will be repopulated if valid for this position)
      if (window.currentAnalysisCache) {
        window.currentAnalysisCache = null;
      }

      // Update the local state first

      setCurrentMoveIdx(idx);

      // Also update the context state to keep them in sync

      dispatch({ type: 'GOTO_MOVE', payload: idx });

      // Then navigate to the new position (which will check cache properly)

      moveNavigation(idx, shouldUpdateContent);
    } else {
    }
  };

  const updateContentForMove = (newContent) => {
    // Clean profanity from move annotations instead of blocking
    const cleanedContent = cleanProfanity(newContent);

    // Ensure we're using the right move ID, with proper fallbacks
    const moveId =
      state.currentMove !== '-1'
        ? state.currentMove
        : currentMoveIdx !== '-1'
        ? currentMoveIdx
        : null;

    // Handle start position content update (index -1)
    if (moveId === '-1') {
      dispatch({
        type: 'UPDATE_START_POSITION_CONTENT',
        payload: cleanedContent,
      });
      return;
    }

    // ENHANCED: Handle start position variation content updates
    if (moveId.startsWith('-1|')) {
      // This is a start position variation move
      const parts = moveId.split('|');
      if (parts.length >= 2) {
        const variationPart = parts[1];
        const [varIdx, moveIdx] = variationPart.split(':').map(Number);

        // Get current content to check if update is needed
        const currentContent =
          state?.startPosition?.variations?.[varIdx]?.[moveIdx]?.content || '';

        // Skip update if content is the same
        if (currentContent === cleanedContent) {
          return;
        }

        // Update the start position variation move content
        dispatch({
          type: 'UPDATE_START_POSITION_VARIATION_MOVE_CONTENT',
          payload: {
            variationIdx: varIdx,
            moveIdx: moveIdx,
            content: cleanedContent,
          },
        });
        return;
      }
    }

    if (!moveId) {
      return;
    }

    // First find the move
    const move = getMove(moves, moveId);
    if (!move) {
      console.warn(`Could not find move at index ${moveId}`);
      return;
    }

    // Store the current move ID to ensure we restore it if needed
    const stableCurrentMove = moveId;

    // Skip update if content is the same to avoid unnecessary rerenders
    if (move.content === cleanedContent) {
      return;
    }

    // Set a flag to skip move index updates during content change
    // This is crucial to prevent the board from resetting
    setSkipMoveIndexUpdate(true);

    // Use the new UPDATE_MOVE_CONTENT action which won't reset board position

    dispatch({
      type: 'UPDATE_MOVE_CONTENT',
      payload: {
        moveId,
        content: cleanedContent,
      },
    });

    // Make sure we're still on the same move
    if (state.currentMove !== stableCurrentMove) {
      dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
    }

    // Reset the skip flag after a longer delay
    setTimeout(() => {
      // Double-check the move hasn't changed before clearing the flag
      if (state.currentMove !== stableCurrentMove) {
        dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
      }

      setSkipMoveIndexUpdate(false);
    }, 500); // Longer delay to ensure state settles
  };

  // Add effect to sync moveContent with current move's content
  useEffect(() => {
    // Skip updates when the annotation is focused to prevent content resetting
    if (isAnnotationFocused || skipMoveIndexUpdate) {
      return;
    }

    // First check if we're tracking state.currentMove properly
    if (state.currentMove !== currentMoveIdx) {
      // Force sync if needed
      setCurrentMoveIdx(state.currentMove);
    }

    // ENHANCED: Handle start position and start position variations
    if (state.currentMove === '-1') {
      // Start position itself
      const content = state?.startPosition?.content || '';
      setMoveContent(content);
    } else if (state.currentMove.startsWith('-1|')) {
      // Start position variation
      const parts = state.currentMove.split('|');
      if (parts.length >= 2) {
        const variationPart = parts[1];
        const [varIdx, moveIdx] = variationPart.split(':').map(Number);

        const content =
          state?.startPosition?.variations?.[varIdx]?.[moveIdx]?.content || '';
        setMoveContent(content);
      } else {
        setMoveContent('');
      }
    } else {
      // Regular move
      const move = getMove(moves, state.currentMove);
      if (move) {
        setMoveContent(move.content || '');
      } else {
        setMoveContent('');
      }
    }
  }, [
    state.currentMove,
    currentMoveIdx,
    moves,
    isAnnotationFocused,
    skipMoveIndexUpdate,
  ]);

  // Add state for bookmark functionality
  const [userFolders, setUserFolders] = useState([]);
  const [selectedBookmarkFolder, setSelectedBookmarkFolder] = useState('NONE');
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderPrivate, setNewFolderPrivate] = useState(false);
  const [bookmarkNotification, setBookmarkNotification] = useState(null);

  const [selectedDifficulty, setSelectedDifficulty] = useState(() => {
    // Get saved difficulty from localStorage or default to "All Levels"
    return localStorage.getItem('preferredDifficulty') || 'All Levels';
  });

  // Function to fetch user's bookmark folders
  const fetchUserFolders = useCallback(async () => {
    if (
      !props.user ||
      !props.user.credentials ||
      !props.user.credentials.userId
    ) {
      // console.error('User not logged in or user ID not available');
      return [];
    }

    try {
      const userId = props.user.credentials.userId;

      // Fetch bookmark folders for the current user
      const folderQuery = query(
        collection(firestore, 'bookmarkFolders'),
        where('userId', '==', userId),
      );

      const folderSnapshot = await getDocs(folderQuery);

      let fetchedFolders = [];

      if (!folderSnapshot.empty) {
        // Process folder documents
        fetchedFolders = folderSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().folderName,
          isPrivate: doc.data().isPrivate,
          games: doc.data().games || [],
          folderIndex:
            doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999,
        }));
      } else {
      }

      // Ensure default folders exist
      let mergedFolders = [...fetchedFolders];

      // Check if Featured folder exists, if not add it
      if (!fetchedFolders.some((folder) => folder.name === 'Featured')) {
        mergedFolders.push({
          name: 'Featured',
          isPrivate: false,
          games: [],
          folderIndex: 0,
        });
      }

      // Check if Safe folder exists, if not add it
      if (!fetchedFolders.some((folder) => folder.name === 'Safe')) {
        mergedFolders.push({
          name: 'Safe',
          isPrivate: true,
          games: [],
          folderIndex: 0,
        });
      }

      // Sort folders
      mergedFolders.sort((a, b) => {
        // First sort by privacy
        if (a.isPrivate !== b.isPrivate) {
          return a.isPrivate ? 1 : -1;
        }
        // Then sort by folderIndex
        return (a.folderIndex || 999) - (b.folderIndex || 999);
      });

      setUserFolders(mergedFolders);
      return mergedFolders;
    } catch (error) {
      console.error('Error fetching user folders:', error);
      return [];
    }
  }, [props.user]);

  // Fetch folders when component mounts or user changes
  useEffect(() => {
    fetchUserFolders();
  }, [fetchUserFolders]);

  // Show bookmark notification
  const showBookmarkNotification = (message, type = 'success') => {
    setBookmarkNotification({
      message,
      type,
    });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setBookmarkNotification(null);
    }, 3000);
  };

  // Add state for player data
  const [playerData, setPlayerData] = useState({
    povPlayer: ['', ''],
    oppositePlayer: ['', ''],
  });

  // State to track if player panels are expanded
  const [playerPanelsExpanded, setPlayerPanelsExpanded] = useState(false);

  // Rate limit modal state
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);

  // Check panel state periodically
  useEffect(() => {
    const checkPanelState = () => {
      if (boardRef.current && boardRef.current.areBothPlayerPanelsExpanded) {
        setPlayerPanelsExpanded(boardRef.current.areBothPlayerPanelsExpanded());
      }
    };

    // Check immediately and then set up an interval
    checkPanelState();
    const interval = setInterval(checkPanelState, 200);

    return () => clearInterval(interval);
  }, []);

  // Add handler to toggle both player panels
  const handleAddPlayersAndRatings = () => {
    if (boardRef.current) {
      // If both panels are expanded, close them; otherwise open them
      if (
        boardRef.current.areBothPlayerPanelsExpanded &&
        boardRef.current.areBothPlayerPanelsExpanded()
      ) {
        boardRef.current.closeBothPlayerPanels();
      } else if (boardRef.current.forceOpenBothPlayerPanels) {
        boardRef.current.forceOpenBothPlayerPanels();
      }
    }
  };

  // Handle player data changes
  const handlePlayerDataChange = (newPlayerData) => {
    setPlayerData(newPlayerData);
  };

  // Clear player data (for new games)
  const clearPlayerData = () => {
    // Clear localStorage
    localStorage.removeItem('playerInfoW_player');
    localStorage.removeItem('playerInfoW_rating');
    localStorage.removeItem('playerInfoB_player');
    localStorage.removeItem('playerInfoB_rating');

    // Reset state
    setPlayerData({
      povPlayer: ['', ''],
      oppositePlayer: ['', ''],
    });

    // Close panels and clear localStorage expansion state
    localStorage.setItem('playerInfoW_expanded', 'false');
    localStorage.setItem('playerInfoB_expanded', 'false');

    // If board ref exists, close the panels and clear internal data
    if (boardRef.current) {
      if (boardRef.current.closeBothPlayerPanels) {
        boardRef.current.closeBothPlayerPanels();
      }
      if (boardRef.current.clearInternalPlayerData) {
        boardRef.current.clearInternalPlayerData();
      }
    }
  };

  // Handle saving game to bookmark folder
  const saveGameToFolder = async (gameId, folderName) => {
    if (folderName === 'NONE') {
      return; // Don't save to any folder
    }

    try {
      // Check if user is logged in
      if (
        !props.user ||
        !props.user.credentials ||
        !props.user.credentials.userId
      ) {
        // console.error('User not logged in or user ID not available');
        showBookmarkNotification(
          'You must be logged in to bookmark games',
          'error',
        );
        return false;
      }

      const userId = props.user.credentials.userId;

      // Find the folder to get its privacy setting
      const folder = userFolders.find((f) => f.name === folderName);
      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        showBookmarkNotification(`Folder "${folderName}" not found`, 'error');
        return false;
      }

      // Check if the folder document already exists in Firestore
      const folderQuery = query(
        collection(firestore, 'bookmarkFolders'),
        where('userId', '==', userId),
        where('folderName', '==', folderName),
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (folderSnapshot.empty) {
        // Folder doesn't exist yet, create a new one with the game at index 0
        const newFolderData = {
          userId: userId,
          folderName: folderName,
          isPrivate: folder.isPrivate,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          folderIndex: 0,
          games: [
            {
              gameId: gameId,
              index: 0,
              createdAt: new Date().toISOString(),
            },
          ],
        };

        // Add a new document to the "bookmarkFolders" collection
        const docRef = await addDoc(
          collection(firestore, 'bookmarkFolders'),
          newFolderData,
        );

        showBookmarkNotification(
          `Game saved to folder "${folderName}"`,
          'success',
        );
        return true;
      } else {
        // Folder exists, update it by adding the game at index 0 and reindexing others
        const folderDoc = folderSnapshot.docs[0];
        const folderData = folderDoc.data();

        // Check if the game is already in the folder
        const existingGameIndex = folderData.games
          ? folderData.games.findIndex((game) => game.gameId === gameId)
          : -1;

        if (existingGameIndex !== -1) {
          // Game already exists, just update its index to 0 and reindex others
          const updatedGames = [...folderData.games];
          const existingGame = updatedGames.splice(existingGameIndex, 1)[0];

          // Increment index of all other games
          updatedGames.forEach((game) => {
            game.index += 1;
          });

          // Add the existing game at index 0
          existingGame.index = 0;
          updatedGames.unshift(existingGame);

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString(),
          });

          showBookmarkNotification(
            `Game updated in folder "${folderName}"`,
            'success',
          );
          return true;
        } else {
          // Game doesn't exist in the folder, add it at index 0
          const updatedGames = folderData.games || [];

          // Increment index of all existing games
          updatedGames.forEach((game) => {
            game.index += 1;
          });

          // Add the new game at index 0
          updatedGames.unshift({
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString(),
          });

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString(),
          });

          showBookmarkNotification(
            `Game saved to folder "${folderName}"`,
            'success',
          );
          return true;
        }
      }
    } catch (error) {
      console.error('Error saving bookmark: ', error);
      showBookmarkNotification(
        'Failed to save bookmark. Please try again.',
        'error',
      );
      return false;
    }
  };

  // Create a new folder and add game to it
  const createNewFolderAndAddGame = async (gameId, folderName, isPrivate) => {
    try {
      // Check if user is logged in
      if (
        !props.user ||
        !props.user.credentials ||
        !props.user.credentials.userId
      ) {
        // console.error('User not logged in or user ID not available');
        showBookmarkNotification(
          'You must be logged in to bookmark games',
          'error',
        );
        return false;
      }

      const userId = props.user.credentials.userId;

      // Validate folder name
      if (!folderName || !folderName.trim()) {
        console.error('Folder name is empty');
        showBookmarkNotification('Please enter a folder name', 'error');
        return false;
      }

      // Check if folder with this name already exists
      const folderQuery = query(
        collection(firestore, 'bookmarkFolders'),
        where('userId', '==', userId),
        where('folderName', '==', folderName.trim()),
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (!folderSnapshot.empty) {
        console.error(`Folder "${folderName.trim()}" already exists`);
        showBookmarkNotification(
          `Folder "${folderName.trim()}" already exists`,
          'error',
        );
        return false;
      }

      // Get the highest folderIndex in the appropriate category
      const categoryFolders = userFolders.filter(
        (f) =>
          f.isPrivate === isPrivate &&
          f.name !== 'Featured' &&
          f.name !== 'Safe',
      );

      const maxIndex =
        categoryFolders.length > 0
          ? Math.max(...categoryFolders.map((f) => f.folderIndex || 0), 0)
          : 0;

      // Create new folder with the game at index 0
      const newFolderData = {
        userId: userId,
        folderName: folderName.trim(),
        isPrivate: isPrivate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        folderIndex: maxIndex + 1, // Add to the end of the appropriate category
        games: [
          {
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString(),
          },
        ],
      };

      // Add a new document to the "bookmarkFolders" collection
      const docRef = await addDoc(
        collection(firestore, 'bookmarkFolders'),
        newFolderData,
      );

      // Show success notification
      showBookmarkNotification(
        `Game saved to new folder "${folderName.trim()}"`,
        'success',
      );

      // Update folder list
      await fetchUserFolders();

      return true;
    } catch (error) {
      console.error('Error creating new folder:', error);
      showBookmarkNotification(
        'Failed to create folder. Please try again.',
        'error',
      );
      return false;
    }
  };

  // Add a helper function to check if the current game is a Challenge

  // PREVIOUS LOGIC:
  // const isFormValid = () => {
  //   const hasTags = tags.length >= 1 && tags.length <= 2;
  //   const hasValidCaption = gameCaption.trim().length >= 2;
  //   const hasRequiredMoves = gameMetadata.gameMode !== 'Puzzle' || moves.length > 0;
  //   return hasTags && hasValidCaption && hasRequiredMoves;
  // };
  const isChallenge = () => {
    return tags.some((tag) => tag.value === 'Challenge');
  };

  // Add this new function to check if form is valid
  const isFormValid = () => {
    const hasTags = tags.length >= 1 && tags.length <= 2;
    const hasValidCaption = gameCaption.trim().length >= 2;
    // No need to check for profanity here since we clean it on submission
    // For Challenges, no moves required
    // For Puzzles, moves are required
    // For all other types (Lessons and defaults), moves are required
    const hasRequiredMoves = isChallenge() || moves.length > 0;

    return hasTags && hasValidCaption && hasRequiredMoves;
  };

  const onSubmitGame = async () => {
    // Clean profanity from title before submission
    const cleanedGameCaption = cleanProfanity(gameCaption);

    // Rate limiting check for game submissions
    const userTier = 'free'; // TODO: Get actual user tier from props/context
    const config = getRateLimit('POST_GAME', userTier);
    const rateKey = buildGlobalRateKey(
      props?.user?.credentials?.userId,
      'postGame',
    );

    const isLimited = checkRateLimit({
      key: rateKey,
      limit: config.limit,
      windowMs: config.windowMs,
    });

    if (isLimited) {
      setShowRateLimitModal(true);
      return;
    }

    // Don't check for moves for Challenges
    if (gameMetadata.gameMode === 'Puzzle' && moves.length === 0) {
      alert('Please add moves for Puzzle mode');
      return;
    }

    // For non-Challenges without the Challenge tag, require moves
    if (!isChallenge() && moves.length === 0) {
      alert('Please add at least one move');
      return;
    }

    // Get the user's handle from props
    const userHandle = props.user?.credentials?.handle;
    let gameData; // Declare gameData outside try block for error handling access

    try {
      if (!userHandle) {
        console.error(
          'User handle not found. Cannot post game without a handle.',
        );
        alert(
          'Could not add game: User information is missing. Please try logging in again.',
        );
        return;
      }

      // Log basic game data for debugging purposes
      // console.log('🎯 Preparing game submission:', {
      //   caption: cleanedGameCaption,
      //   fen:
      //     isChallenge() || !moves.length
      //       ? rootFen
      //       : gameMetadata.gameMode === undefined
      //       ? rootFen
      //       : game.history({ verbose: true })[0]?.before || rootFen,
      //   movesCount: moves.length,
      //   tags: tags.map((tag) => tag.value),
      //   isChallenge: isChallenge(),
      //   isPrivate: gameMetadata.isPrivate,
      //   difficulty: selectedDifficulty,
      // });

      // First, handle bookmark data preparation
      let shouldCreateNewFolder = false;
      let newFolder = null;

      if (isCreatingNewFolder && newFolderName.trim()) {
        shouldCreateNewFolder = true;
        newFolder = {
          name: newFolderName.trim(),
          isPrivate: newFolderPrivate,
        };
      }

      // Check for and log variation captions before submitting
      const movesWithVariationCaptions = moves.filter(
        (move) =>
          move.variations &&
          move.variations.length > 0 &&
          move.variationCaptions &&
          Object.keys(move.variationCaptions).length > 0,
      );

      if (movesWithVariationCaptions.length > 0) {
        //
        //

        // Log detailed structure of each move with variation captions
        //
        movesWithVariationCaptions.forEach((move, idx) => {
          logVariationStructure(move);
        });
      } else {
        //
      }

      // Get current player data from the board
      const currentPlayerData = boardRef.current?.getCurrentPlayerData?.() || {
        povPlayer: ['', ''],
        oppositePlayer: ['', ''],
      };

      // Prepare the game data
      gameData = {
        caption: cleanedGameCaption,
        fen:
          isChallenge() || !moves.length
            ? rootFen
            : gameMetadata.gameMode === undefined
            ? rootFen
            : game.history({ verbose: true })[0]?.before || rootFen,
        comments: [],
        suggestedMoves: [],
        moves: moves,
        starredMoves: starredMoves,
        tags: tags.map((tag) => tag.value),
        pov: orientation === 'white',
        // Add the handle as the primary creator identifier
        creatorHandle: userHandle,
        // Keep the userID as a reference for backend purposes
        creator: props.user.credentials.userId,
        // Add a flag to indicate this is a challenge game
        isChallenge: isChallenge(),
        // Add isPrivate flag for game privacy --> Still need to update VIEWER logic for this, and more.
        isPrivate: gameMetadata.isPrivate,
        difficulty: selectedDifficulty,
        // Add the startPosition data from GameStateContext (sanitized for Firestore)
        startPosition: (() => {
          try {
            const sanitized = sanitizeStartPositionForFirestore(
              state.startPosition || createStartPosition(rootFen),
            );
            // Final validation - ensure the result is JSON serializable
            JSON.stringify(sanitized);

            // Backend doesn't support startPosition.variations yet
            // Store variations in a compatible format for now
            if (sanitized.variations && sanitized.variations.length > 0) {
              // console.log(
              //   '🔄 Backend limitation: Storing starting position variations in compatible format',
              // );

              // Convert variations to a simple string format that can be stored
              const variationsBackup = JSON.stringify(sanitized.variations);

              // Store variations data in a hidden metadata field instead of content
              return {
                fen: sanitized.fen,
                content: sanitized.content, // Keep content clean
                arrows: sanitized.arrows,
                highlightedSquares: sanitized.highlightedSquares,
                variationCaptions: sanitized.variationCaptions, // Include variation captions
                // Store encoded variations in a metadata field that backend can handle
                _variationsData: variationsBackup, // Hidden field for variations backup
              };
            }

            // console.log(
            //   '🚀 Submitting startPosition without variations (backend compatible)',
            // );
            return {
              fen: sanitized.fen,
              content: sanitized.content,
              arrows: sanitized.arrows,
              highlightedSquares: sanitized.highlightedSquares,
              variationCaptions: sanitized.variationCaptions, // Include variation captions
            };
          } catch (sanitizationError) {
            console.error(
              'Sanitization failed, using minimal startPosition:',
              sanitizationError,
            );
            // Minimal fallback - just the essential FEN
            return {
              fen: String(
                rootFen ||
                  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
              ),
              content: '',
              variations: [],
            };
          }
        })(),
        // Add player data if any fields are filled
        povPlayer:
          currentPlayerData.povPlayer[0] || currentPlayerData.povPlayer[1]
            ? currentPlayerData.povPlayer
            : null,
        oppositePlayer:
          currentPlayerData.oppositePlayer[0] ||
          currentPlayerData.oppositePlayer[1]
            ? currentPlayerData.oppositePlayer
            : null,
      };

      // Detailed logging of the complete game data being sent to server
      // console.log('🚀 SUBMITTING GAME DATA TO SERVER:', {
      //   dataSize: JSON.stringify(gameData).length,
      //   movesCount: gameData.moves.length,
      //   hasStartPosition: !!gameData.startPosition,
      //   startPositionStructure: gameData.startPosition
      //     ? {
      //         fen: gameData.startPosition.fen,
      //         hasVariations: Array.isArray(gameData.startPosition.variations),
      //         variationsCount: gameData.startPosition.variations?.length || 0,
      //         variationsDetail: gameData.startPosition.variations?.map((v) => ({
      //           isArray: Array.isArray(v),
      //           length: v?.length || 0,
      //           firstMove: v?.[0]?.notation,
      //         })),
      //       }
      //     : null,
      //   firstMove: gameData.moves[0]?.notation,
      //   lastMove: gameData.moves[gameData.moves.length - 1]?.notation,
      //   tagsCount: gameData.tags.length,
      //   isPrivate: gameData.isPrivate,
      //   isChallenge: gameData.isChallenge,
      // });

      // Final validation check - ensure data is JSON serializable
      try {
        const testSerialization = JSON.stringify(gameData);
        // console.log(
        //   '✅ Game data JSON serialization successful, size:',
        //   testSerialization.length,
        // );
      } catch (serializationError) {
        console.error('❌ Game data serialization failed:', serializationError);
        alert(
          'Game data contains invalid elements. Please try simplifying your annotations.',
        );
        return;
      }

      const newGame = await postGame(gameData);

      // Log after API call
      //
      //
      //
      //

      const newGameId = newGame?.data?.id;

      if (newGameId) {
        let bookmarkSuccess = true;

        // Handle bookmark folder selection
        try {
          if (shouldCreateNewFolder) {
            // Create new folder and add game to it
            bookmarkSuccess = await createNewFolderAndAddGame(
              newGameId,
              newFolder.name,
              newFolder.isPrivate,
            );
          } else if (selectedBookmarkFolder !== 'NONE') {
            // Add to existing folder
            bookmarkSuccess = await saveGameToFolder(
              newGameId,
              selectedBookmarkFolder,
            );
          }
        } catch (bookmarkError) {
          console.error('Error handling bookmark:', bookmarkError);
          bookmarkSuccess = false;
        }

        // Add a slight delay to ensure notifications are seen before navigation
        setTimeout(
          () => {
            // Navigate to the new game regardless of bookmark status
            history.push(`/games/${newGameId}?ctx=newGame`);
          },
          bookmarkSuccess ? 1000 : 0,
        ); // If bookmark successful, wait 1 second for notification
      } else {
        props.history.push('/');
      }
    } catch (err) {
      console.error('❌ ERROR POSTING GAME:', {
        error: err,
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        requestSize: gameData
          ? JSON.stringify(gameData).length + ' characters'
          : 'unknown',
        hasStartPosition: gameData ? !!gameData.startPosition : false,
        startPositionVariationsCount:
          gameData?.startPosition?.variations?.length || 0,
      });

      // Provide a more helpful error message
      let errorMessage = 'Could not add game. ';

      // Check if there's a response with an error message
      if (err.response && err.response.data) {
        if (err.response.data.error) {
          errorMessage += err.response.data.error;
        } else if (err.response.data.err) {
          errorMessage += err.response.data.err;
        } else if (err.response.status === 500) {
          errorMessage +=
            'Server error. This may be due to complex data structures.';
        }
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Please try again.';
      }

      alert(errorMessage);
    }
  };

  const resetPost = () => {
    // Dispatch custom event to signal board to clear lastMoveRef
    window.dispatchEvent(new Event('chess-game-reset'));

    // Clear player data for new games
    clearPlayerData();

    setGameCaption('');
    setPgn('');
    dispatch({ type: 'SET_MOVES', payload: [] }); // during full reset
    setCurrentMoveIdx('-1');
    setGameMetadata({
      visibility: 'Public',
      isPrivate: false,
      gameMode: null, // Reset gameMode
    });
    dispatch({ type: 'SET_STARRED_MOVES', payload: [] });
    setMoveContent('');
    setVariationContent('');
    setTags([]);
    setGame(new Chess());
    setSkipMoveIndexUpdate(false); // Make sure to reset this flag
    setOpenModalType(''); // Ensure modal is closed
    // +++ Reset puzzle mode context +++
    setIsPuzzleMode(false);

    // Properly reset the board FEN to starting position
    const startFEN = CHESSBOARD.START_POSITION;
    setRootFen(startFEN);

    // Update orientation and game state context
    dispatch({ type: 'SET_ORIENTATION', payload: 'white' });
    dispatch({ type: 'SET_INITIAL_POSITION', payload: startFEN });

    // Create and update the startPosition structure
    const newStartPosition = createStartPosition(startFEN);
    dispatch({ type: 'UPDATE_START_POSITION', payload: newStartPosition });

    // Use setTimeout to ensure board gets properly updated
    setTimeout(() => {
      // Try to directly update the board's FEN using the ref
      if (boardRef.current && boardRef.current.updateBoardFEN) {
        boardRef.current.updateBoardFEN(startFEN);

        // Also update orientation
        if (boardRef.current.updateOrientation) {
          boardRef.current.updateOrientation('white');
        }
      } else {
        // Fallback to using events

        window.dispatchEvent(
          new CustomEvent('board-root-fen-changed', {
            detail: {
              fen: startFEN,
              orientation: 'white',
            },
          }),
        );
      }

      // Clear any cached analysis
      if (window.stockfishAnalysisManager) {
        window.stockfishAnalysisManager.clearCache();
      }

      // Clear analysis cache
      window.currentAnalysisCache = null;
      window.moveClassificationData = null;
    }, 100);

    setStep(0);
  };

  const onRootFenChange = (newFen) => {
    newFen = newFen || undefined;
    setRootFen(newFen);
    setPgn('');

    try {
      const game = new Chess(newFen);
      setGame(game);
      setGameMetadata({
        ...gameMetadata,
        fen: newFen,
      });

      // Update the board orientation based on the turn in the FEN
      const turn = getTurnInFen(newFen);
      const newOrientation = turn === 'w' ? 'white' : 'black';

      // Update orientation in the global state
      dispatch({ type: 'SET_ORIENTATION', payload: newOrientation });

      // Update the initial position in the GameStateContext with startPosition support
      dispatch({ type: 'SET_INITIAL_POSITION', payload: newFen });

      // Create and update the startPosition structure
      const newStartPosition = createStartPosition(newFen);
      dispatch({ type: 'UPDATE_START_POSITION', payload: newStartPosition });

      // Clear any cached analysis since we're changing the starting position
      window.currentAnalysisCache = null;
      window.moveClassificationData = null;

      // Reset everything else
      setGameCaption('');
      setPgn('');
      dispatch({ type: 'SET_MOVES', payload: [] });
      setCurrentMoveIdx('-1');

      // Use setTimeout to avoid potential race conditions with event handlers
      setTimeout(() => {
        // Try to directly update the board's FEN using the ref
        if (boardRef.current && boardRef.current.updateBoardFEN) {
          boardRef.current.updateBoardFEN(newFen);

          // Also directly update the board's orientation using the ref method
          if (boardRef.current.updateOrientation) {
            boardRef.current.updateOrientation(newOrientation);
          }
        } else {
          // Fallback to using events
          window.dispatchEvent(
            new CustomEvent('board-root-fen-changed', {
              detail: {
                fen: newFen,
                // Don't include orientation in the event to avoid dispatch errors
                orientation: null,
              },
            }),
          );
        }

        // FORCE STOCKFISH ANALYSIS FOR NEW STARTING POSITION
        // Clear the analysis manager cache for the new FEN
        if (window.stockfishAnalysisManager) {
          window.stockfishAnalysisManager.clearCache();
        }

        // Force immediate analysis for the new starting position

        window.dispatchEvent(
          new CustomEvent('stockfish-analyze', {
            detail: {
              fen: newFen,
              forceCalculation: true,
              priority: 'high',
              reason: 'root-fen-changed',
            },
          }),
        );

        //
        //
      }, 100); // Slightly longer delay to ensure all state updates are complete

      return true;
    } catch (err) {
      console.error('[2AddGame] Error changing root FEN:', err);
      return false;
    }
  };

  // First, let's add a state for tracking game result information
  // Add this near the other state variables
  const [gameResultInfo, setGameResultInfo] = useState({
    result: null, // "draw", "checkmate", "normal", etc.
    winner: null, // "white", "black"
    reason: null, // Specific reason like "stalemate", "insufficient material", etc.
  });

  // Now modify the handlePgnParse function to handle result notations
  const handlePgnParse = (pgn) => {
    try {
      // Create a fresh Chess instance to ensure we're starting from scratch
      const newGame = new Chess();

      if (pgn.trim() !== '') {
        // First, extract any game result notation from the end of the PGN
        let cleanedPgn = pgn.trim();
        let gameResult = {
          result: 'normal',
          winner: null,
          reason: null,
        };

        // Check for standard result notations at the end of the PGN
        if (cleanedPgn.match(/\s+1-0\s*$/)) {
          gameResult = {
            result: 'normal',
            winner: 'white',
            reason: 'resignation',
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+1-0\s*$/, '');
        } else if (cleanedPgn.match(/\s+0-1\s*$/)) {
          gameResult = {
            result: 'normal',
            winner: 'black',
            reason: 'resignation',
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+0-1\s*$/, '');
        } else if (cleanedPgn.match(/\s+1\/2-1\/2\s*$/)) {
          gameResult = {
            result: 'draw',
            winner: null,
            reason: 'agreement',
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+1\/2-1\/2\s*$/, '');
        } else if (cleanedPgn.match(/\s+\*\s*$/)) {
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+\*\s*$/, '');
        }

        // Split the PGN into moves
        const moves = cleanedPgn
          .split(/\d+\./)
          .filter((move) => move.trim() !== '');

        // Apply each move to the fresh game instance
        let moveApplied = false;
        moves.forEach((move) => {
          // Split by whitespace to get individual moves
          const moveParts = move.trim().split(/\s+/);

          // Process each potential move
          for (const potentialMove of moveParts) {
            // Skip empty strings or non-move notations
            if (
              !potentialMove ||
              potentialMove.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)
            ) {
              continue;
            }

            try {
              // Attempt to apply the move

              if (newGame.move(potentialMove, { sloppy: true })) {
                moveApplied = true;

                // Check if this move is a checkmate
                if (potentialMove.includes('#')) {
                  gameResult.result = 'checkmate';
                  gameResult.winner =
                    newGame.turn() === 'w' ? 'black' : 'white'; // Opposite of whose turn it is
                  gameResult.reason = 'checkmate';
                }
              }
            } catch (moveError) {
              console.warn(
                `[AddGame] Failed to apply move: ${potentialMove}`,
                moveError,
              );
              // Continue to next move even if this one fails
            }
          }
        });

        // Only update game state if at least one move was applied
        if (moveApplied) {
          // Update game with the new instance
          setGame(newGame);
          setCurrentMoveIdx(`${newGame.history().length - 1}`);

          // Create entirely new moves array
          const newMoves = newGame
            .history()
            .map((move) => ({ notation: move, content: '', arrows: [] }));

          dispatch({ type: 'SET_MOVES', payload: newMoves });

          // Save the game result info for use in the Moves component
          setGameResultInfo(gameResult);

          // Always set the root FEN to the standard starting position for PGN
          setRootFen(
            'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
          );
        } else {
          console.error(
            '[AddGame] No valid moves could be applied from the PGN',
          );
        }
      } else {
        throw new Error('PGN is empty');
      }
    } catch (error) {
      console.error('Error parsing PGN:', error);
    }
  };

  const onStarMoveClicked = () => {
    if (state.currentMove === '-1') return;

    //
    //

    // Use state.currentMove which is the string ID of the move
    dispatch({ type: 'TOGGLE_STAR_MOVE', payload: state.currentMove });
  };

  const handlePremiumClick = () => {
    window.open('/premium', '_blank');
    // The current page/modal will remain unchanged
  };

  useEffect(() => {
    // Check if there's imported PGN data from PlayChess
    const importedPGN = localStorage.getItem('analysisImportPGN');
    if (importedPGN) {
      // Handle the imported PGN
      handlePgnParse(importedPGN);

      // Clear the localStorage after importing
      localStorage.removeItem('analysisImportPGN');
    }
  }, []);

  // Add function to handle difficulty change
  const handleDifficultyChange = (value) => {
    setSelectedDifficulty(value);
    localStorage.setItem('preferredDifficulty', value);
  };

  // Track the temporary new FEN position when closing FENBuilder
  const [pendingFen, setPendingFen] = useState(null);
  const [pendingPgn, setPendingPgn] = useState(null);

  // Track whether to skip the GameModeChooser modal
  const [skipGameModeChooser, setSkipGameModeChooser] = useState(false);

  // Effect to handle showing GameModeChooser after FENBuilder is closed
  useEffect(() => {
    // If FENBuilder just closed and we have pending data
    if (openModalType === '' && (pendingFen || pendingPgn)) {
      // IMPORTANT: Reset all board state completely before loading new position
      // This ensures we're not building on top of previous board state

      // Clear all game-related state
      dispatch({ type: 'SET_STARRED_MOVES', payload: [] });
      setCurrentMoveIdx('-1');
      setMoveContent('');
      setVariationContent('');

      if (pendingFen) {
        // Important: Clear moves before setting FEN
        dispatch({ type: 'SET_MOVES', payload: [] });

        // Update the board FEN directly if possible
        if (boardRef.current && boardRef.current.updateBoardFEN) {
          boardRef.current.updateBoardFEN(pendingFen);
        }

        // Also update the game state
        onRootFenChange(pendingFen);
        setPendingFen(null); // Clear pending data
      }

      if (pendingPgn) {
        // Check if we have comprehensive moves (object) vs simple PGN (string)
        if (typeof pendingPgn === 'object' && pendingPgn.comprehensiveMoves) {
          // Reset game state completely for comprehensive moves
          setGame(new Chess());
          setRootFen(CHESSBOARD.START_POSITION);
          dispatch({ type: 'SET_MOVES', payload: [] });

          // Use the comprehensive moves directly - these are already in the proper format
          const comprehensiveMoves = pendingPgn.comprehensiveMoves;

          // Set the moves array directly
          dispatch({ type: 'SET_MOVES', payload: comprehensiveMoves });

          // Navigate to the end of the game
          if (comprehensiveMoves.length > 0) {
            const lastMoveIndex = comprehensiveMoves.length - 1;
            setTimeout(() => {
              moveNavigation(lastMoveIndex, false);
            }, 100);
          }
        } else {
          // Fallback to original PGN processing for simple strings

          // For PGN, we need to completely reset the game first
          // Create a brand new Chess instance
          setGame(new Chess());
          // Reset the root FEN to the standard starting position
          setRootFen(CHESSBOARD.START_POSITION);
          // Clear existing moves before parsing PGN
          dispatch({ type: 'SET_MOVES', payload: [] });
          // Now process the PGN with a clean slate
          const pgnString =
            typeof pendingPgn === 'object' ? pendingPgn.pgn : pendingPgn;
          handlePgnParse(pgnString);
        }

        setPendingPgn(null); // Clear pending data
      }

      // Only show GameModeChooser if we're not skipping it (for PGN uploads)
      if (skipGameModeChooser) {
        setSkipGameModeChooser(false); // Reset for next time
      } else {
        setOpenModalType('GameModeChooser');
      }
    }
  }, [openModalType, pendingFen, pendingPgn, skipGameModeChooser]);

  // Add a useEffect to track modal changes
  useEffect(() => {}, [openModalType]);

  // Add effect to set up the control panel tooltips styling
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'engine-panel-tooltip-styles-addgame';
    styleEl.textContent = `
      .engine-panel-btn .engine-panel-tooltip {
        visibility: hidden;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: -30px;
        background-color: rgba(0, 0, 0, 0.8);
        color: gold;
        text-align: center;
        border-radius: 4px;
        padding: 5px 8px;
        white-space: nowrap;
        z-index: 200;
        font-size: 12px;
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      .engine-panel-btn:hover .engine-panel-tooltip {
        visibility: visible;
        opacity: 1;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      if (document.getElementById('engine-panel-tooltip-styles-addgame')) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);

  // Add effect to update boardStartingTurn when rootFen changes
  useEffect(() => {
    try {
      // Extract turn from FEN
      const fenTurn = getTurnInFen(rootFen);
      setBoardStartingTurn(fenTurn || 'w');
    } catch (error) {
      console.error('Error parsing FEN for turn:', error);
      setBoardStartingTurn('w'); // Default to white if there's an error
    }
  }, [rootFen]);

  // +++ Add effect to handle full game analysis completion +++
  useEffect(() => {
    window.onFullAnalysisComplete = (updatedMoves) => {
      //
      // Update the state with the analysis results from Stockfish.js
      dispatch({ type: 'SET_MOVES', payload: updatedMoves });
    };

    // Cleanup function to remove the global callback when the component unmounts
    return () => {
      delete window.onFullAnalysisComplete;
    };
  }, [dispatch]); // Add setMoves as a dependency

  // Add state for current FEN position
  const [currentFen, setCurrentFen] = useState('');
  const [openingInfo, setOpeningInfo] = useState(null);

  // State for SAP analysis data
  const [sapLiveAnalysis, setSapLiveAnalysis] = useState(null);
  const [sapIsCalculating, setSapIsCalculating] = useState(false);
  const [sapEngineReady, setSapEngineReady] = useState(false);

  // Callback to receive analysis data from SAP
  const handleSapAnalysisUpdate = useCallback((analysisData) => {
    // console.log('ABC123 2AddGame received analysis update from SAP for PVD:', {
    //   source: analysisData?.source,
    //   isCalculating: analysisData?.isCalculating,
    //   isEngineReady: analysisData?.isEngineReady,
    //   hasPvs: !!analysisData?.pvs,
    //   pvsLength: analysisData?.pvs?.length,
    //   fen: analysisData?.fen?.substring(0, 20) + '...',
    //   positionFEN: analysisData?.positionFEN?.substring(0, 20) + '...',
    // });

    // Always update engine ready state
    if (analysisData?.isEngineReady !== undefined) {
      setSapEngineReady(analysisData.isEngineReady);
    }

    // Handle calculating state vs analysis results
    if (analysisData?.isCalculating) {
      setSapIsCalculating(true);
      // Don't update analysis data while calculating to prevent showing stale data
    } else {
      setSapIsCalculating(false);

      // Enhanced logic: Don't overwrite better analysis with worse analysis
      setSapLiveAnalysis((currentAnalysis) => {
        // If we don't have any analysis yet, use the new data
        if (!currentAnalysis) {
          // console.log('ABC123 Setting initial analysis data:', {
          //   depth: analysisData?.depth,
          //   pvs: analysisData?.pvs?.length,
          // });
          return analysisData;
        }

        // If the new analysis is for a different FEN, use it
        const currentFEN = (
          currentAnalysis.positionFEN ||
          currentAnalysis.fen ||
          ''
        )
          .split(' ')
          .slice(0, 4)
          .join(' ');
        const newFEN = (analysisData?.positionFEN || analysisData?.fen || '')
          .split(' ')
          .slice(0, 4)
          .join(' ');

        if (currentFEN !== newFEN) {
          // console.log('ABC123 New FEN detected, updating analysis:', {
          //   oldFEN: currentFEN.substring(0, 20) + '...',
          //   newFEN: newFEN.substring(0, 20) + '...',
          //   depth: analysisData?.depth,
          // });
          return analysisData;
        }

        // For the same FEN, only upgrade if the new analysis is better
        const currentDepth = currentAnalysis.depth || 0;
        const newDepth = analysisData?.depth || 0;
        const currentPvCount = currentAnalysis.pvs?.length || 0;
        const newPvCount = analysisData?.pvs?.length || 0;

        // Don't downgrade from d=15 with 3 PVs to anything less complete
        if (
          currentDepth >= 15 &&
          currentPvCount >= 3 &&
          (newDepth < 15 || newPvCount < 3)
        ) {
          // console.log(
          //   'ABC123 PREVENTING DOWNGRADE from complete d=15 analysis:',
          //   {
          //     current: { depth: currentDepth, pvs: currentPvCount },
          //     new: { depth: newDepth, pvs: newPvCount },
          //   },
          // );
          return currentAnalysis; // Keep the better analysis
        }

        // Allow upgrades (higher depth or same depth with more PVs)
        if (
          newDepth > currentDepth ||
          (newDepth === currentDepth && newPvCount > currentPvCount)
        ) {
          // console.log('ABC123 UPGRADING analysis:', {
          //   from: { depth: currentDepth, pvs: currentPvCount },
          //   to: { depth: newDepth, pvs: newPvCount },
          // });
          return analysisData;
        }

        // For same or worse analysis, keep what we have
        // console.log('ABC123 KEEPING existing analysis (same/worse new data):', {
        //   current: { depth: currentDepth, pvs: currentPvCount },
        //   new: { depth: newDepth, pvs: newPvCount },
        // });
        return currentAnalysis;
      });
    }
  }, []);
  // Reference for tracking API call timeout
  const fetchTimeoutRef = useRef(null);
  // Track last 20 fetched FENs to prevent duplicate requests
  const lastFetchedFens = useRef([]);

  //

  // Function to fetch opening information from Firebase Cloud Function
  const getOpeningFromFEN = async (fen) => {
    try {
      const res = await fetch(
        'https://us-central1-chessisfun-66b29.cloudfunctions.net/getOpening',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fen }),
        },
      );
      return await res.json();
    } catch (error) {
      console.error('Error fetching opening information:', error);
      return null;
    }
  };

  // Add state for FEN tracking like ViewGame
  const lastFenRef = useRef(null);

  // Helper function to get FEN directly from moves array for variation moves
  const getStoredFENFromMoves = (moves, moveId) => {
    if (!moves || !moveId || moveId === '-1') {
      return null;
    }

    // Handle starting position variations (e.g., "-1|0:0")
    if (moveId.startsWith('-1|')) {
      if (!state || !state.startPosition || !state.startPosition.variations) {
        return null;
      }

      try {
        const parts = moveId.split('|');
        if (parts.length >= 2) {
          const variationPart = parts[1];
          const [varIdx, moveIdx] = variationPart.split(':').map(Number);

          const variation = state.startPosition.variations[varIdx];
          const move = variation?.[moveIdx];

          if (move && move.fen) {
            // console.log(
            //   '✅ [2AddGame] Found stored FEN for starting position variation:',
            //   moveId,
            //   move.fen,
            // );
            return move.fen;
          }
        }
      } catch (err) {
        console.error(
          '[2AddGame] Error getting stored FEN for starting position variation:',
          err,
        );
      }

      return null;
    }

    // Handle regular variation moves (e.g., "4|0:2")
    if (moveId.includes('|')) {
      const parts = moveId.split('|');
      const mainLineIdx = parseInt(parts[0], 10);

      if (parts.length === 2 && parts[1].includes(':')) {
        const [varIdx, moveIdx] = parts[1].split(':').map(Number);

        // Navigate to the specific variation move
        const parentMove = moves[mainLineIdx];
        const variation = parentMove?.variations?.[varIdx];
        const variationMove = variation?.[moveIdx];

        return variationMove?.fen || null;
      }
    } else {
      // Handle main line moves
      const moveIndex = parseInt(moveId, 10);
      if (moveIndex >= 0 && moveIndex < moves.length) {
        const moveData = moves[moveIndex];

        return moveData?.fen || null;
      }
    }

    return null;
  };

  // FEN TRACKING - Connect currentFen to existing game state (SIMPLIFIED VERSION)
  useEffect(() => {
    // CRITICAL FIX: Add small delay to allow recordMove batch updates to complete
    const timeoutId = setTimeout(() => {
      let newFen = null;

      // Calculate FEN based on current move
      if (state.currentMove === '-1') {
        // Start position - use state.initialFEN which should be correct
        newFen =
          state.initialFEN ||
          'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      } else {
        // Try to get stored FEN directly from moves array first (both main line and variations)
        const storedFEN = getStoredFENFromMoves(state.moves, state.currentMove);

        if (storedFEN) {
          newFen = storedFEN;
        } else {
          // Fallback to calculation only if no stored FEN is found
          console.warn(
            '[2AddGame] No stored FEN found for move',
            state.currentMove,
            ', falling back to calculation',
          );
          newFen = getCurrentBoardPosition(
            state.moves,
            state.currentMove,
            state.initialFEN,
          );
        }
      }

      if (newFen && newFen !== lastFenRef.current) {
        lastFenRef.current = newFen; // Update the ref
        setCurrentFen(newFen);
        // Update global window.currentFEN for compatibility
        window.currentFEN = newFen;

        // CRITICAL FIX: Update Redux state to keep state.currentFEN synchronized
        setContextFEN(newFen);

        // TRIGGER AUTOMATIC OPENING DETECTION
        handleOpeningDetection(newFen, state.currentMove);

        // Check for cached analysis
        const cachedAnalysis = checkForCachedAnalysis(
          state.currentMove,
          newFen,
        );
        if (cachedAnalysis) {
        } else {
        }
      } else {
      }
    }, 10); // Small delay to allow recordMove batch updates to complete

    return () => clearTimeout(timeoutId);
  }, [
    state.currentMove,
    state.moves,
    state.initialFEN,
    checkForCachedAnalysis,
    setContextFEN,
  ]);

  // Simplified opening detection function (extracted from the complex handleFenChange)
  const handleOpeningDetection = (fen, currentMoveId) => {
    // Clear any pending timeout to implement debouncing
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Start a new debounced request
    fetchTimeoutRef.current = setTimeout(() => {
      const targetMoveIndex = parseInt(currentMoveId);

      // Skip opening lookups after move 12 or for invalid moves
      if (targetMoveIndex > 12 || currentMoveId === '-1') {
        return;
      }

      // Check if the target move already has opening data
      const targetMoveData = getMove(state.moves, currentMoveId);
      if (targetMoveData?.opening) {
        setOpeningInfo(targetMoveData.opening);
        return;
      }

      // Create a unique key for this FEN + move combination to avoid duplicates
      const fenKey = `${fen.split(' ')[0]}-move${currentMoveId}`;
      if (lastFetchedFens.current.includes(fenKey)) {
        return;
      }

      // Add this FEN+move to our tracking array (keep only the last 12)
      lastFetchedFens.current.push(fenKey);
      if (lastFetchedFens.current.length > 12) {
        lastFetchedFens.current.shift(); // Remove oldest FEN
      }

      // Call the Cloud Function to get opening information
      getOpeningFromFEN(fen)
        .then((opening) => {
          if (opening && (opening.name || opening.eco)) {
            setOpeningInfo(opening);

            // Validate that we have a valid move to save to
            if (
              currentMoveId !== '-1' &&
              targetMoveIndex >= 0 &&
              targetMoveIndex < state.moves.length
            ) {
              // Use UPDATE_MOVE_PROPERTIES to save the opening data
              dispatch({
                type: 'UPDATE_MOVE_PROPERTIES',
                payload: {
                  moveId: currentMoveId,
                  properties: { opening: opening },
                },
              });

              // Add a verification step after a short delay to confirm the data was saved
              setTimeout(() => {
                const latestMoves = window.moves || state.moves;
                const updatedMoveData = getMove(latestMoves, currentMoveId);

                if (updatedMoveData?.opening) {
                } else {
                  console.error(
                    '[2AddGame] ❌ Opening data was NOT saved to move',
                    currentMoveId,
                  );
                }
              }, 100);
            } else {
            }
          } else {
          }
        })
        .catch((error) => {
          console.error('[2AddGame] ❌ Error fetching opening data:', error);
        });
    }, 500); // 500ms debounce delay
  };

  // Simplified FEN change handler - now only handles board updates, not opening detection
  const handleFenChange = (fen) => {
    if (fen && fen !== currentFen) {
      setCurrentFen(fen);

      // Note: Opening detection is now handled by the FEN tracking useEffect above
    }
  };

  // Add direct connection to the Board component
  useEffect(() => {
    // Simplified global function - just handle FEN updates from board
    window.addGameHandleFenChange = (fen) => {
      // Simply update the FEN - the FEN tracking useEffect will handle the rest
      handleFenChange(fen);
    };

    // The event handler that will call our global function
    const handleBoardFenUpdate = (event) => {
      if (event.detail && event.detail.fen) {
        window.addGameHandleFenChange(event.detail.fen);
      }
    };

    // Attach the event listener

    window.addEventListener('board-fen-updated', handleBoardFenUpdate);

    // Add handler for forced analysis refresh (used after sequential moves)
    const handleForceAnalysisRefresh = (event) => {
      if (event.detail && event.detail.finalPosition) {
        // Trigger immediate analysis for the final position
        window.dispatchEvent(
          new CustomEvent('stockfish-analyze', {
            detail: {
              fen: event.detail.finalPosition,
              forceCalculation: true,
              priority: 'high',
              reason: event.detail.reason || 'sequential-playback-complete',
            },
          }),
        );
      }
    };

    window.addEventListener(
      'force-analysis-refresh',
      handleForceAnalysisRefresh,
    );

    // Also listen for moves which should trigger FEN checks
    const handlePositionChange = () => {
      if (boardRef.current && boardRef.current.getCurrentFEN) {
        const currentBoardFEN = boardRef.current.getCurrentFEN();
        if (currentBoardFEN) {
          window.addGameHandleFenChange(currentBoardFEN);
        }
      }
    };

    return () => {
      window.removeEventListener('board-fen-updated', handleBoardFenUpdate);
      window.removeEventListener(
        'force-analysis-refresh',
        handleForceAnalysisRefresh,
      );
      delete window.addGameHandleFenChange;
    };
  }, []); // Remove dependencies since we don't need move synchronization anymore

  //

  // Export getCurrentMoveIndex function to window for analysis validation
  useEffect(() => {
    window.getCurrentMoveIndex = () => {
      return state.currentMove;
    };

    return () => {
      delete window.getCurrentMoveIndex;
    };
  }, [state.currentMove]);

  // Global function to get current FEN (used by other components)
  useEffect(() => {
    window.getCurrentFEN = () => {
      return getCurrentBoardPosition(state.moves, state.currentMove, rootFen);
    };
  }, [state.currentMove, state.moves, rootFen]);

  // Add the new Stockfish analysis hook
  // CRITICAL FIX: Use stable FEN calculation to prevent infinite re-renders
  const synchronizedFEN = useMemo(() => {
    // Use state.currentFEN if it's available and not empty
    if (state.currentFEN && state.currentFEN.trim() !== '') {
      return state.currentFEN;
    }

    // Fallback calculation only if state.currentFEN is not available
    if (state.currentMove === '-1') {
      return (
        state.initialFEN ||
        'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
      );
    } else {
      const storedFEN = getStoredFENFromMoves(state.moves, state.currentMove);
      return (
        storedFEN ||
        getCurrentBoardPosition(
          state.moves,
          state.currentMove,
          state.initialFEN,
        )
      );
    }
  }, [state.currentFEN, state.currentMove, state.initialFEN]); // Removed state.moves to prevent constant recalc

  const {
    analysis: stockfishAnalysis,
    isAnalyzing: stockfishIsAnalyzing,
    analysisError: stockfishAnalysisError,
    forceAnalysisRefresh,
    cancelAnalysis: cancelStockfishAnalysis,
    manager: stockfishManager,
  } = useStockfishAnalysisManager({
    currentMoveIdx: state.currentMove,
    moves: state.moves,
    currentFEN: synchronizedFEN, // Use synchronized FEN instead of state.currentFEN
    gameState: state,
    dispatch,
    enabled: true, // Always enable for background calculation
  });

  // Extract evaluation from stockfish analysis for the evaluation bar
  const currentEvaluation = React.useMemo(() => {
    if (!stockfishAnalysis) {
      return 0; // Default to 0 when no analysis
    }

    // Try to get the best evaluation from the first PV line
    if (stockfishAnalysis.pvs && stockfishAnalysis.pvs.length > 0) {
      const firstPV = stockfishAnalysis.pvs[0];

      // Check for normalizedCP first (most reliable)
      if (firstPV.normalizedCP !== undefined && firstPV.normalizedCP !== null) {
        return firstPV.normalizedCP;
      }

      // Fall back to evaluation string
      if (firstPV.evaluation) {
        // Handle mate scores
        if (
          typeof firstPV.evaluation === 'string' &&
          firstPV.evaluation.includes('M')
        ) {
          return firstPV.evaluation;
        }

        // Parse numeric evaluations
        const numericEval = parseFloat(firstPV.evaluation.replace('+', ''));
        if (!isNaN(numericEval)) {
          return numericEval;
        }
      }
    }

    // Try scores array as fallback
    if (stockfishAnalysis.scores && stockfishAnalysis.scores.length > 0) {
      const firstScore = stockfishAnalysis.scores[0];

      // Handle mate scores
      if (typeof firstScore === 'string' && firstScore.includes('M')) {
        return firstScore;
      }

      // Parse numeric scores
      const numericScore = parseFloat(firstScore.replace('+', ''));
      if (!isNaN(numericScore)) {
        return numericScore;
      }
    }

    return 0; // Default fallback
  }, [stockfishAnalysis]);

  // Replace the old analysis effect with this simplified version
  useEffect(() => {
    // The new analysis system handles everything automatically
    // We just need to expose some functions globally for backward compatibility

    // Expose global functions for components that still use the old API
    window.getCurrentMoveIndex = () => state.currentMove;
    window.checkForCachedAnalysis = (moveIndex, targetFEN = null) => {
      // Check if move has analysis
      if (moveIndex === '-1') return false;

      const moveObj = getMoveObject(moveIndex);
      return moveObj ? !!moveObj.analysis : false;
    };
    window.getCachedAnalysisForFEN = (fen) => {
      // For backward compatibility - check current analysis
      if (stockfishAnalysis && stockfishAnalysis.positionFEN) {
        const currentKey = stockfishAnalysis.positionFEN
          .split(' ')
          .slice(0, 4)
          .join(' ');
        const searchKey = fen.split(' ').slice(0, 4).join(' ');
        return currentKey === searchKey ? stockfishAnalysis : null;
      }
      return null;
    };
    window.updatePositionForAnalysis = (fen) => {
      // Skip analysis for starting position variations to prevent conflicts
      if (state.currentMove && state.currentMove.startsWith('-1|')) {
        return;
      }

      // CRITICAL FIX: Actually use the FEN parameter and update context first
      if (fen && fen !== state.currentFEN) {
        // Update the context FEN first to ensure synchronization
        setContextFEN(fen);

        // Small delay to ensure context update completes before triggering analysis
        setTimeout(() => {
          forceAnalysisRefresh();
        }, 5);
      } else {
        // Force analysis refresh for the current position
        forceAnalysisRefresh();
      }
    };

    // Expose the analysis manager for cache management
    window.stockfishAnalysisManager = stockfishManager;

    // Add event listener for forced stockfish analysis
    const handleStockfishAnalyze = (event) => {
      if (event.detail && event.detail.forceCalculation) {
        const { fen, reason } = event.detail;

        // If we have a specific FEN and we're at the starting position (-1),
        // we need to handle this specially since forceAnalysisRefresh only works for moves
        if (
          fen &&
          state.currentMove === '-1' &&
          reason === 'root-fen-changed'
        ) {
          // Clear cache first
          if (stockfishManager) {
            stockfishManager.clearCache();

            // Request analysis directly for the starting position
            stockfishManager
              .requestAnalysis(fen, '-1', null, null)
              .then((analysis) => {
                // Update the global cache so UI components can access it
                window.currentAnalysisCache = analysis;
              })
              .catch((error) => {
                console.error(
                  '[2AddGame] Starting position analysis failed:',
                  error,
                );
              });
          }
        } else {
          // Use the normal force refresh for other cases
          forceAnalysisRefresh();
        }
      }
    };

    window.addEventListener('stockfish-analyze', handleStockfishAnalyze);

    return () => {
      window.removeEventListener('stockfish-analyze', handleStockfishAnalyze);
      delete window.stockfishAnalysisManager;
    };
  }, [state.currentMove, forceAnalysisRefresh, stockfishManager]); // Removed stockfishAnalysis from dependencies to prevent re-render loop

  // Separate effect to update global cache when analysis changes - prevents re-render loops
  useEffect(() => {
    if (stockfishAnalysis) {
      // GUARD: Only update cache if analysis is complete to prevent unnecessary re-renders
      if (
        stockfishAnalysis.isComplete !== false &&
        stockfishAnalysis.pvs?.length > 0
      ) {
        window.currentAnalysisCache = stockfishAnalysis;
      }
    }
  }, [stockfishAnalysis]);

  // Helper function to get move object by ID
  const getMoveObject = (moveId) => {
    if (!moveId || moveId === '-1') return null;

    if (!moveId.includes('|')) {
      const idx = parseInt(moveId, 10);
      return idx >= 0 && idx < state.moves.length ? state.moves[idx] : null;
    }

    // Handle variations - simplified version
    const parts = moveId.split('|');
    let currentNode = null;
    const mainIdx = parseInt(parts[0], 10);

    if (mainIdx >= 0 && mainIdx < state.moves.length) {
      currentNode = state.moves[mainIdx];

      for (let i = 1; i < parts.length; i++) {
        if (!parts[i].includes(':')) continue;
        const [varIdx, moveIdx] = parts[i].split(':').map(Number);

        if (i < parts.length - 1) {
          if (
            currentNode.variations &&
            varIdx < currentNode.variations.length &&
            moveIdx < currentNode.variations[varIdx].length
          ) {
            currentNode = currentNode.variations[varIdx][moveIdx];
          } else {
            return null;
          }
        } else {
          if (
            currentNode.variations &&
            varIdx < currentNode.variations.length &&
            moveIdx < currentNode.variations[varIdx].length
          ) {
            return currentNode.variations[varIdx][moveIdx];
          }
        }
      }
    }

    return null;
  };

  if (loading) {
    return <ScreamSkeleton />;
  }

  const currentMove = getMove(moves, currentMoveIdx);
  const isLesson = () => {
    return tags.some((tag) => tag.value === 'Lesson');
  };

  // Helper to debug variation structure
  const logVariationStructure = (move, indent = 0) => {
    if (!move) return;

    const indentStr = ' '.repeat(indent);
    //

    if (move.variationCaptions) {
      //
    }

    if (move.variations && move.variations.length > 0) {
      //
      move.variations.forEach((variation, varIdx) => {
        //
        if (Array.isArray(variation)) {
          variation.forEach((varMove, moveIdx) => {
            //
          });
        }
      });
    }
  };

  // Add function to update variation content
  const updateVariationContent = async (newContent) => {
    // Clean profanity from variation content instead of blocking
    const cleanedContent = cleanProfanity(newContent);

    //

    // Ensure we're in a variation
    if (!isMoveInVariation(state.currentMove, moves)) {
      //
      return;
    }

    // ENHANCED: Handle start position variations
    if (state.currentMove.startsWith('-1|')) {
      // This is a start position variation
      const parts = state.currentMove.split('|');
      if (parts.length >= 2) {
        const variationPart = parts[1];
        const [varIdx] = variationPart.split(':').map(Number);

        // Skip update if content is the same
        const currentContent =
          state?.startPosition?.variationCaptions?.[varIdx] || '';
        if (currentContent === cleanedContent) {
          return;
        }

        // Set flags to prevent board updates
        setSkipMoveIndexUpdate(true);

        // Store a stable reference to current move ID
        const stableCurrentMove = state.currentMove;

        // Use action to update start position variation caption
        await new Promise((resolve) => {
          dispatch({
            type: 'UPDATE_START_POSITION_VARIATION_CAPTION',
            payload: {
              variationIdx: varIdx,
              content: cleanedContent,
            },
          });
          // Give the reducer time to process the action
          setTimeout(resolve, 50);
        });

        // Set flag to prevent immediate sync from useEffect
        setJustUpdatedVariationContent(true);

        // Clear the flag after update with longer timeout
        setTimeout(() => {
          setJustUpdatedVariationContent(false);
        }, 300);

        // Make sure we stay on the current move
        if (state.currentMove !== stableCurrentMove) {
          dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
        }

        // Reset the skip flag after a delay
        setTimeout(() => {
          if (state.currentMove !== stableCurrentMove) {
            dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
          }
          setSkipMoveIndexUpdate(false);
        }, 200);

        return;
      }
    }

    // Get the variation indices for regular variations
    const [moveRef, variationIdx] = getMoveAndVariationIndices(
      state.currentMove,
      moves,
    );

    if (!moveRef) {
      console.warn(
        'Could not find move reference for variation content update',
      );
      return;
    }

    // Skip update if content is the same
    const currentContent = moveRef.variationCaptions?.[variationIdx] || '';
    if (currentContent === cleanedContent) {
      //
      return;
    }

    //

    // Log move structure before update
    //
    logVariationStructure(moveRef);

    // Set flags to prevent board updates
    setSkipMoveIndexUpdate(true);

    // Store a stable reference to current move ID
    const stableCurrentMove = state.currentMove;

    // We need to handle variation captions differently since they're stored in a separate structure
    // Find the index of the parent move in the main line
    const mainMoveIdx = parseInt(state.currentMove.split('|')[0], 10);

    if (isNaN(mainMoveIdx) || mainMoveIdx < 0 || mainMoveIdx >= moves.length) {
      console.warn('Invalid main move index for variation content update');
      return;
    }

    // Use our new action that specifically handles variation captions

    await new Promise((resolve) => {
      dispatch({
        type: 'UPDATE_VARIATION_CAPTION',
        payload: {
          mainMoveId: mainMoveIdx,
          variationIdx,
          content: cleanedContent,
        },
      });
      // Give the reducer time to process the action
      setTimeout(resolve, 50);
    });

    // Set flag to prevent immediate sync from useEffect
    setJustUpdatedVariationContent(true);

    // Verify the save worked by checking the updated state
    setTimeout(() => {
      const updatedMoveRef = getMove(moves, mainMoveIdx);
      const savedCaption =
        updatedMoveRef?.variationCaptions?.[variationIdx] || '';

      logVariationStructure(updatedMoveRef);

      // Clear the flag after verification
      setTimeout(() => {
        setJustUpdatedVariationContent(false);
      }, 100);
    }, 100);

    // Make sure we stay on the current move
    if (state.currentMove !== stableCurrentMove) {
      //
      dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
    }

    // Reset the skip flag after a delay
    setTimeout(() => {
      //

      // Double-check the move hasn't changed before clearing the flag
      if (state.currentMove !== stableCurrentMove) {
        //
        dispatch({ type: 'GOTO_MOVE', payload: stableCurrentMove });
      }

      setSkipMoveIndexUpdate(false);
      //
    }, 500); // Longer delay to ensure state settles
  };

  // Function to fetch opening data for the first 12 moves of the game
  const fetchOpeningsForGame = async () => {
    setIsLoadingOpenings(true);

    try {
      // Get FENs for the first 12 moves
      const fenPositions = [];

      // Add the starting position (root FEN)
      fenPositions.push(rootFen);

      // We need to create a temporary game instance to play through the moves and capture FENs
      const tempGame = new Chess(rootFen);

      // Get moves history from the current game instance
      const moveHistory = game.history({ verbose: true });

      //

      // Get FENs for up to 12 moves by playing each move and recording position
      for (let i = 0; i < Math.min(12, moveHistory.length); i++) {
        try {
          // Apply the move to our temporary game
          tempGame.move(moveHistory[i]);
          // Record the position after this move
          fenPositions.push(tempGame.fen());
        } catch (err) {
          console.error('Error applying move for FEN extraction:', err);
        }
      }

      //

      // Skip if we don't have any positions
      if (fenPositions.length === 0) {
        setIsLoadingOpenings(false);
        setOpeningOptions([
          {
            value: 'no-opening',
            label: 'Continue without opening',
            className: 'tag-openings',
            color: TAGS.Openings.color,
            background: TAGS.Openings.background,
          },
        ]);
        return;
      }

      // Fetch opening data for each position
      const openingPromises = fenPositions.map((fen) => getOpeningFromFEN(fen));
      const openingResults = await Promise.all(openingPromises);

      //

      // Filter results to only include valid openings
      const validOpenings = openingResults
        .filter((opening) => opening && opening.eco) // Only keep results with ECO codes
        .filter((opening) => {
          // Check if this ECO code is in our allowedEcoGroups
          for (const [groupName, ecoCodes] of Object.entries(
            allowedEcoGroups,
          )) {
            if (ecoCodes.includes(opening.eco)) {
              // Add the group name to the opening
              opening.groupName = groupName;
              return true;
            }
          }
          return false;
        });

      //
      //

      // Extract unique opening groups
      const uniqueGroups = Array.from(
        new Set(validOpenings.map((o) => o.groupName)),
      );

      //

      // Convert to dropdown options
      const options = uniqueGroups.map((groupName) => ({
        value: groupName,
        label: groupName,
        className: 'tag-openings',
        width: '100%',
        color: TAGS.Openings.color,
        background: TAGS.Openings.background,
      }));

      // // Add back option
      // options.unshift({
      //   value: 'back',
      //   label: '← Back to tags',
      //   className: 'tag-openings',
      //   color: 'white',
      //   background: '#333333'
      // });

      // Always add a "Continue without opening" option
      options.push({
        value: 'no-opening',
        label: 'Continue without opening',
        className: 'tag-openings',
        color: 'red',
        background: 'TAGS.Openings.background',
      });

      //

      setOpeningOptions(options);
    } catch (error) {
      console.error('Error fetching openings:', error);
      // Provide a fallback option
      setOpeningOptions([
        {
          value: 'back',
          label: '← Back to tags',
          className: 'tag-openings',
          color: 'white',
          background: '#333333',
        },
        {
          value: 'no-opening',
          label: 'Continue without opening',
          className: 'tag-openings',
          color: TAGS.Openings.color,
          background: TAGS.Openings.background,
        },
      ]);
    } finally {
      setIsLoadingOpenings(false);
    }
  };

  // Handle opening selection
  const handleOpeningSelect = (opening) => {
    if (!opening) return;

    if (opening.value === 'back') {
      // Return to tag selection mode
      setIsOpeningSelectionMode(false);
      return;
    }

    if (opening.value === 'no-opening') {
      // Just keep the Openings tag and exit opening selection mode
      setIsOpeningSelectionMode(false);
      return;
    }

    // Add both the general "Openings" tag and the specific opening tag
    const openingTagValue = `Openings - ${opening.label}`;
    const openingTag = {
      value: openingTagValue,
      label: openingTagValue,
      className: 'tag-openings',
      color: TAGS.Openings.color,
      background: TAGS.Openings.background,
    };

    // Check if we already have the "Openings" tag
    const hasOpeningsTag = tags.some((tag) => tag.value === 'Openings');

    if (hasOpeningsTag) {
      // Replace the existing Openings tag with the specific one
      const newTags = tags.filter((tag) => tag.value !== 'Openings');
      setTags([...newTags, openingTag]);
    } else {
      // Add the specific opening tag
      setTags([...tags, openingTag]);
    }

    // Exit opening selection mode
    setIsOpeningSelectionMode(false);
  };

  // Handle back button in opening selection mode
  const handleOpeningSelectionBack = () => {
    setIsOpeningSelectionMode(false);
  };

  return (
    <div className="app-container">
      {openModalType === 'FAQAndSettings' && (
        <FAQAndSettingsModal source="addgame" onClose={() => setOpenModalType('')} />
      )}

      {openModalType === 'FENBuilder' && (
        <FENBuilderModal
          fen={
            game?.fen() ||
            'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
          }
          onFENUpdate={(fen, gameResult) => {
            // Use our Board component's handleFENUpdate function directly
            // This will update the board state immediately
            if (boardRef.current && boardRef.current.updateBoardFEN) {
              boardRef.current.updateBoardFEN(fen);

              // Also update the game state in AddGame component
              onRootFenChange(fen);
            } else {
              // Fallback to the old method if the ref isn't available
              setPendingFen(fen);
            }
          }}
          onPGNUpdate={(pgn, options) => {
            // Check if we have comprehensive moves from the new processor
            if (
              options?.comprehensiveMoves &&
              options.comprehensiveMoves.length > 0
            ) {
              // Store both PGN and the comprehensive moves for processing after modal closes
              setPendingPgn({
                pgn: pgn,
                comprehensiveMoves: options.comprehensiveMoves,
                gameResult: options.gameResult,
                moveList: options.moveList,
              });
            } else {
              // Fallback to simple PGN string

              setPendingPgn(pgn);
            }
          }}
          onClose={(params) => {
            // Check if we should skip the GameModeChooser (for PGN uploads)
            if (params?.skipGameModeChooser) {
              setSkipGameModeChooser(true);
            }
            setOpenModalType('');
          }}
        />
      )}
      {/*openModalType === 'Drafts' && (
        <DraftsModal
          open={true}
          onClose={() => setOpenModalType('')}
          primaryAction={() =>
            setGameMetadata((metadata) =>
              setGameMetadata({ ...gameMetadata, visibility: 'Drafts' }),
            )
          }
        />
      )}*/}
      {openModalType === 'GameModeChooser' && (
        <GameModeChooserModal
          onGameModeUpdate={(mode) => {
            setGameMetadata((gameMetadata) => ({
              ...gameMetadata,
              gameMode: mode,
            }));
            // Only add tags for specific modes with complete styling
            if (mode === 'Puzzle') {
              setTags((tags) => [
                ...tags,
                {
                  value: 'Puzzle',
                  label: TAGS.Puzzle.label,
                  className: TAGS.Puzzle.className,
                  color: TAGS.Puzzle.color,
                  background: TAGS.Puzzle.background,
                },
              ]);
              // +++ Set puzzle mode in context +++
              setIsPuzzleMode(true);
            } else if (mode === 'Challenge') {
              setTags((tags) => [
                ...tags,
                {
                  value: 'Challenge',
                  label: TAGS.Challenge.label,
                  className: TAGS.Challenge.className,
                  color: TAGS.Challenge.color,
                  background: TAGS.Challenge.background,
                },
              ]);
              // For Challenge mode, automatically switch to the Save & Share tab
              setStep(1);
            } else if (mode === 'Lesson') {
              setTags((tags) => [
                ...tags,
                {
                  value: 'Lesson',
                  label: TAGS.Lesson.label,
                  className: TAGS.Lesson.className,
                  color: TAGS.Lesson.color,
                  background: TAGS.Lesson.background,
                },
              ]);
            }
            // Close the controls panel when position is loaded
            setShowControls(false);
            // Close the modal

            setOpenModalType('');
          }}
          onClose={() => {
            setOpenModalType('');
          }}
        />
      )}
      {openModalType === 'DeleteConfirm' && (
        <DeleteConfirmModal
          onConfirm={() => {
            resetPost();
            setOpenModalType('');
          }}
          onClose={() => setOpenModalType('')}
        />
      )}
      {openModalType === 'StartPosition' && (
        <StartPositionModal
          currentFen={localStorage.getItem('currentPositionFen') || game.fen()}
          currentMoveIdx={currentMoveIdx}
          onPositionUpdate={(fen, options) => {
            if (options.resetMoves) {
              // Reset all moves and set the current position as start

              // First reset everything
              resetPost();

              // Then set the new FEN using the board ref
              if (boardRef.current && boardRef.current.updateBoardFEN) {
                boardRef.current.updateBoardFEN(fen);
                // Also update the game state
                onRootFenChange(fen);
              } else {
                // Fallback
                onRootFenChange(fen);
              }

              // Use setTimeout to ensure this runs after state updates
              setTimeout(() => {
                setOpenModalType('GameModeChooser');
              }, 50);
            } else {
              // Keep trailing moves - implementation

              // Get the current move index as a number
              const currentIdx = parseInt(options.currentMoveIdx);

              if (currentIdx >= 0 && currentIdx < moves.length) {
                try {
                  // First, let's completely reset the state before building it back up
                  resetPost();

                  // Update the board FEN directly using the ref
                  if (boardRef.current && boardRef.current.updateBoardFEN) {
                    boardRef.current.updateBoardFEN(fen);
                  }

                  // Start with a clean chess instance using the current FEN
                  const newGame = new Chess(fen);

                  // Get trailing moves
                  const trailingMoves = moves.slice(currentIdx + 1);

                  // Now we need to properly transform these moves to work in the new context
                  // We'll create a temporary game to validate and transform these moves
                  const tempGame = new Chess(fen);
                  const transformedMoves = [];

                  // Create a separate game instance to track the original moves for detailed info
                  const originalGame = new Chess(rootFen);
                  // Play moves up to current index in original game to get to the same point
                  for (let i = 0; i <= currentIdx; i++) {
                    if (i < moves.length) {
                      try {
                        originalGame.move(moves[i].notation);
                      } catch (e) {
                        console.error(`Error replaying original move ${i}:`, e);
                      }
                    }
                  }

                  // Try to apply trailing moves to temp game and get proper notations
                  let allMovesApplied = true;
                  for (let i = 0; i < trailingMoves.length; i++) {
                    try {
                      const originalMove = trailingMoves[i];
                      const originalIndex = currentIdx + 1 + i;

                      // Try several methods to make the move successfully
                      let appliedMove = null;

                      // Method 1: Try using the original game's verbose move history
                      if (
                        originalIndex < game.history({ verbose: true }).length
                      ) {
                        const verboseMove = game.history({ verbose: true })[
                          originalIndex
                        ];
                        if (verboseMove) {
                          try {
                            appliedMove = tempGame.move({
                              from: verboseMove.from,
                              to: verboseMove.to,
                              promotion: verboseMove.promotion || undefined,
                            });
                          } catch (e) {
                            console.warn(
                              `Failed with from/to for move ${i}:`,
                              e,
                            );
                          }
                        }
                      }

                      // Method 2: Try direct notation as fallback
                      if (!appliedMove) {
                        try {
                          appliedMove = tempGame.move(originalMove.notation);
                        } catch (e) {
                          console.warn(
                            `Failed with notation for move ${i}:`,
                            e,
                          );
                        }
                      }

                      if (appliedMove) {
                        // Successfully applied the move, add it to our transformed list
                        transformedMoves.push({
                          notation: appliedMove.san, // Use the SAN from the applied move
                          content: originalMove.content || '',
                          arrows: originalMove.arrows || [],
                        });
                      } else {
                        console.error(
                          `Could not apply move ${i}. Stopping sequence.`,
                        );
                        allMovesApplied = false;
                        break;
                      }
                    } catch (err) {
                      console.error(`Error processing move ${i}:`, err);
                      allMovesApplied = false;
                      break;
                    }
                  }

                  if (transformedMoves.length > 0) {
                    // Set a flag to skip the automatic move application in useMoveIndex
                    setSkipMoveIndexUpdate(true);

                    // Set the root FEN to the new starting position
                    setRootFen(fen);

                    // Update game state with the new transformed moves
                    setGame(newGame);
                    dispatch({ type: 'SET_MOVES', payload: transformedMoves });
                    setCurrentMoveIdx('-1');

                    // Adjust starred moves indexes
                    const adjustedStarredMoves = starredMoves
                      .filter(
                        (idx) =>
                          parseInt(idx) > currentIdx &&
                          parseInt(idx) - (currentIdx + 1) <
                            transformedMoves.length,
                      )
                      .map((idx) =>
                        (parseInt(idx) - (currentIdx + 1)).toString(),
                      );

                    dispatch({
                      type: 'SET_STARRED_MOVES',
                      payload: adjustedStarredMoves,
                    });

                    // Reset the skip flag after a timeout to allow state updates to complete
                    setTimeout(() => {
                      setSkipMoveIndexUpdate(false);
                    }, 300);

                    // Show game mode chooser dialog
                    setOpenModalType('GameModeChooser');
                  } else {
                    // If we couldn't apply any moves, just reset and use the FEN
                    resetPost();
                    onRootFenChange(fen);
                    setOpenModalType('GameModeChooser');
                  }
                } catch (err) {
                  console.error('Error processing trailing moves:', err);
                  // If there's an error, fallback to reset
                  resetPost();
                  onRootFenChange(fen);
                  setOpenModalType('GameModeChooser');
                }
              } else {
                // Invalid move index, just reset and use the FEN
                resetPost();
                onRootFenChange(fen);
                setOpenModalType('GameModeChooser');
              }
            }

            // Clean up local storage
            localStorage.removeItem('currentPositionFen');
          }}
          onClose={() => {
            setOpenModalType('');
            // Clean up local storage
            localStorage.removeItem('currentPositionFen');
          }}
        />
      )}
      <div
        className="column"
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'start',
          overflow: 'hidden',
          gap: '5px',
          width: 'auto',
          padding: '0px',
        }}
      >
        <ChessAnalysisWrapper>
          <div
            style={{
              width: '100%',
              borderRadius: '10px',
              padding: '0px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                }}
              >
                <div
                  style={{
                    height: `${boardSize + 189}px`,
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '10px',
                  }}
                >
                  {localShowStockfishEval && (
                    <StockfishEval
                      evaluation={currentEvaluation}
                      orientation={orientation}
                      height="100%"
                      width={30}
                      boardSize={boardSize}
                      isAnalyzing={stockfishIsAnalyzing}
                    />
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    ref={containerRef}
                    className="main-top-section"
                    style={{
                      width: boardSize + 10,
                      height: boardSize + 10,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      marginBottom: '10px',
                      transition: 'width 0.2s, height 0.2s',
                      padding: 0,
                      borderRadius: '5px',
                    }}
                  >
                    {/* Display opening information above the board */}
                    {openingInfo && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-40px',
                          left: '0',
                          width: '100%',
                          textAlign: 'center',
                          padding: '5px',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'gold',
                          borderRadius: '5px',
                          fontSize: '14px',
                          fontStyle: 'italic',
                          zIndex: 10,
                          color: 'gold',
                        }}
                      >
                        {openingInfo.name && <span>{openingInfo.name}</span>}
                        {openingInfo.eco && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '14px',
                              opacity: 0.9,
                            }}
                          >
                            ECO: {openingInfo.eco}
                          </span>
                        )}
                      </div>
                    )}
                    <div
                      style={{
                        width: boardSize,
                        height: boardSize,
                        boxSizing: 'border-box',
                        boxShadow: '0 0 20px 5px rgba(0, 0, 0, 0.7)',
                        borderRadius: '5px',
                      }}
                    >
                      {openModalType === 'FENBuilder' ? (
                        <div
                          style={{
                            width: boardSize,
                            height: boardSize,
                            background: 'gray',
                          }}
                        ></div>
                      ) : (
                        <>
                          <SetupDefaults />
                          <ChessAnalysisWrapper>
                            <BoardThree
                              ref={boardRef}
                              boardSize={boardSize}
                              boardOrientation={orientation}
                              boardTheme={
                                localStorage.getItem('boardTheme') || 'blue'
                              }
                              showBoardNotation={
                                localStorage.getItem('showCoordinates') === null
                                  ? true
                                  : localStorage.getItem('showCoordinates') ===
                                    'true'
                              }
                              // Add view mode (editable)
                              isViewMode={false}
                              // Don't show collapsed icons on board in AddGame anymore
                              showCollapsedIcons={false}
                              // Add player data handling
                              onPlayerDataChange={handlePlayerDataChange}
                              onFenChange={handleFenChange}
                              // Add starting variation limit callback
                              onStartingVariationLimit={() => setShowStartingVariationLimitModal(true)}
                              // game={game}
                              // orientation={orientation}
                              // moves={moves}
                              // setMoves={setMoves}
                              // currentMove={currentMoveIdx}
                            />
                          </ChessAnalysisWrapper>
                        </>
                        // previous board
                        // <Board
                        //   game={game}
                        //   orientation={orientation}
                        //   moves={moves}
                        //   setMoves={setMoves}
                        //   currentMove={currentMoveIdx}
                        //   setCurrentMove={setCurrentMoveIdx}
                        //   isCommenting={false}
                        //   boardSize={boardSize}
                        //   boardTheme={localStorage.getItem('boardTheme') || 'blue'}
                        //   isDraggingDisabled={isChallenge()}
                        //   gameResultInfo={gameResultInfo}
                        //   onFenChange={handleFenChange}
                        // />
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: '0',
                      width: boardSize + 10,
                      position: 'relative',
                      height: '150px', // Fixed height for component
                    }}
                  >
                    {/* Always render Stockfish but control visibility with CSS */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                        visibility: localShowStockfish ? 'visible' : 'hidden',
                      }}
                    >
                      {/* <Stockfish isViewGame={false} /> */}
                      <StockfishAnalysisPanel
                        fen={currentFen}
                        analysisData={stockfishAnalysis}
                        isAnalyzing={stockfishIsAnalyzing}
                        showStockfish={localShowStockfish}
                        onAnalysisUpdate={handleSapAnalysisUpdate}
                        onMoveSelect={(moveData) => {
                          //

                          // Dispatch the move to the board using window events
                          if (moveData.bestMoveLAN || moveData.lan) {
                            const moveToApply =
                              moveData.bestMoveLAN || moveData.lan;
                            //

                            window.dispatchEvent(
                              new CustomEvent('stockfish-move-selected', {
                                detail: {
                                  move: moveToApply,
                                  san: moveData.bestMoveSAN || moveData.san,
                                  source: 'stockfish-analysis-panel',
                                },
                              }),
                            );
                          }
                        }}
                      />
                    </div>

                    {/* Show ENGINE OFF message when UI is hidden, but engine still runs */}
                    {!localShowStockfish && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          background: 'black',
                          borderRadius: '5px',
                          color: 'gold',
                          width: '100%',
                          height: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontStyle: 'italic',

                          zIndex: 2,
                        }}
                      >
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                          Stockfish Engine Best Moves Hidden
                        </div>
                        <div
                          style={{
                            fontSize: '20px',
                            marginTop: '5px',
                            opacity: 0.8,
                            cursor: 'pointer',
                          }}
                          onClick={handleStockfishToggle}
                        >
                          Click to Reveal...
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ChessAnalysisWrapper>
      </div>

      <div
        className=""
        style={{
          borderRadius: '5px',
          position: 'relative',
          margin: '10px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Add this to prevent content overflow
        }}
      >
        <div className="tab-container">
          {step === 0 && (
            <>
              {/* First bordered group - Moves and Annotation */}
              <div
                style={{
                  border: '5px solid #121212',
                  borderRadius: '5px',
                  padding: '15px',
                  background:
                    'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  height: '100%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '',
                }}
              >
                {/* Second bordered group - Controls */}

                <div
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!showControls && moves?.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '10px 0px',
                        paddingBottom: '0px',
                        position: 'absolute',
                        bottom: '305px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 'calc(100% - 0px)',
                        zIndex: 10,
                      }}
                      className="render-Creator-div"
                    >
                      {/* Star icon and move notation content */}

                      {/* <span
                        style={{
                          height: '62px',
                          minWidth: '150px',
                          width: 'auto',
                          fontSize: '30px',
                          backgroundColor:
                            // Apply correct background color based on move color (white/black)
                            game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? '#333333' // Black move (dark background)
                                : '#ffffff' // White move (light background)
                              : currentMove?.moveNum % 2 === 1
                                ? '#333333' // Black move (dark background) 
                                : '#ffffff', // White move (light background)
                          color:
                            // Apply correct text color based on background
                            game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? '#f0f0f0' // White text on dark background
                                : '#222222' // Dark text on light background
                              : currentMove?.moveNum % 2 === 1
                                ? '#f0f0f0' // White text on dark background
                                : '#222222', // Dark text on light background
                          borderRadius: '5px',
                          marginRight: '10px',
                          border: 'none',
                          boxShadow: '0 0 12px rgba(88, 176, 71, 0.4)',
                          // Only add green border on bottom and right sides
                          borderBottom: '4px solid #58b047',
                          borderRight: '4px solid #58b047',
                          background: currentMove?.notation && starredMoves.includes(currentMoveIdx)
                            ? 'linear-gradient(315deg, black, gold)'  // Add gold gradient for starred moves
                            : game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? 'linear-gradient(to bottom, #333333, #222222)' // Black move gradient
                                : 'linear-gradient(to bottom, #ffffff, #f0f0f0)' // White move gradient
                              : currentMove?.moveNum % 2 === 1
                                ? 'linear-gradient(to bottom, #333333, #222222)' // Black move gradient
                                : 'linear-gradient(to bottom, #ffffff, #f0f0f0)', // White move gradient
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          animation: 'glow-current 2s infinite',
                        }}
                      >
                        {currentMove?.notation ? (
                          <span>
                            {Math.floor((parseInt(currentMoveIdx) + 2) / 2)}.
                            {parseInt(currentMoveIdx) % 2 === 1 ? '.. ' : ' '}
                            {currentMove.notation}
                          </span>
                        ) : (
                          <span className='move-new-game' style={{ background: 'linear-gradient(to bottom, #4ca140, #58b047)', topLeftRadius: '5px', topLeftBorderRadius: '5px' }}>Chess</span>
                        )}
                      </span>
                       */}

                      <div
                        onKeyDown={(e) => {
                          if (
                            [
                              'ArrowRight',
                              'ArrowLeft',
                              'ArrowUp',
                              'ArrowDown',
                              ' ',
                            ].includes(e.key)
                          ) {
                            // Stop event propagation completely
                            e.stopPropagation();
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        {/* Star icon is now integrated into MovesThree component */}

                        {/* Move content textarea is now integrated into MovesThree component */}
                      </div>
                    </div>
                  )}
                  {moves.length === 0 &&
                    !showControls &&
                    gameMetadata.gameMode !== 'Puzzle' && (
                      <div style={{ width: '96%' }}>
                        <button
                          className="FEN-buttons reg-buttons green-hover advanced-setup-button"
                          onClick={() => setOpenModalType('FENBuilder')}
                          style={{
                            flex: '1',
                            fontSize: '35px',
                            border: '3px solid #57b047',
                            color: '#57b047',
                            width: '100%',
                            marginBottom: '15px',
                            height: '80px'
                          }}
                        >
                          Puzzle Builder&nbsp;&nbsp;
                          <i className="fa-solid fa-puzzle-piece"></i>
                        </button>
                      </div>
                    )}
                  {!showControls && gameMetadata.gameMode !== 'Puzzle' ? (
                    // Show the "OPEN CONTROLS" button when controls are hidden AND not in puzzle mode
                    <button
                      className="gray-hover-2"
                      onClick={() => {
                        setShowControls(true);

                        // COMPREHENSIVE GAME OBJECT LOGGING - Single consolidated log
                      }}
                      style={{
                        width: '96%',
                        color: 'gray',
                        border: '3px solid gray',
                        padding: '10px 0px',
                        fontSize: '35px',
                        fontWeight: 'bold',
                        background: 'transparent',
                        height: '80px',
                        borderRadius: '5px',
                        position: 'relative',
                        marginBottom: '15px',

                        display: 'block',
                      }}
                    >
                      Control Panel&nbsp;&nbsp;
                      <i className="fa-solid fa-gear"></i>
                    </button>
                  ) : showControls && gameMetadata.gameMode !== 'Puzzle' ? (
                    // Show the controls section when showControls is true AND not in puzzle mode
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '96%',
                        marginBottom: '10px',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '30px',
                          fontWeight: 'bold',
                          marginBottom: '5px',
                          textAlign: 'center',
                          borderBottom: '3px solid gray',
                          color: 'gray',
                          paddingBottom: '10px',
                          position: 'relative',
                          width: '100%',
                        }}
                      >
                        C O N T R O L&nbsp;&nbsp;&nbsp;P A N E L
                        <i
                          className="fa-solid fa-circle-xmark"
                          style={{
                            position: 'absolute',
                            right: '25px',
                            top: '40%',
                            transform: 'translateY(-50%)',
                            fontSize: '30px',
                            color: '#ff0050',
                            cursor: 'pointer',
                            borderRadius: '5px',
                            padding: '5px',
                          }}
                          onClick={() => setShowControls(false)}
                        />
                      </div>

                      <div
                        className="FEN-buttons-div"
                        style={{
                          marginTop: '10px',
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '8px',
                        }}
                      >
                        <button
                          className={clsx(
                            'FEN-buttons',
                            'reg-buttons',
                            orientation === 'white'
                              ? 'flip-button-white-oriented'
                              : 'flip-button-black-oriented',
                          )}
                          style={{
                            flex: '1',
                            height: '70px',
                            fontSize: '20px',
                            // Remove inline background, border, color styles
                            // border: orientation === 'black' ? '3px solid white' : '3px solid black',
                            // background: orientation === 'black' ? 'black' : 'white',
                            // color: orientation === 'black' ? 'white' : 'black',
                          }}
                          // onClick={() => setOrientation(getFlippedBoardOrientation(orientation))}

                          onClick={() => {
                            dispatch({
                              type: 'SET_ORIENTATION',
                              payload:
                                orientation === 'white' ? 'black' : 'white',
                            });
                            //
                          }}
                        >
                          FLIP &nbsp;
                          <i className="fa-solid fa-retweet"></i>
                        </button>
                        <button
                          className="FEN-buttons reg-buttons white-hover"
                          style={{
                            flex: '1',
                            height: '70px',
                            fontSize: '20px',
                            border: state.moves.length === 0 ? '3px solid #4CAF50' : '3px solid gray',
                            color: state.moves.length === 0 ? '#4CAF50' : '',
                          }}
                          onClick={() => setOpenModalType('FAQAndSettings')}
                        >
                          FAQs
                        </button>

                        <button
                          className="delete-button"
                          style={{
                            flex: '1',
                            height: '70px',
                            fontSize: '20px',
                            border: '3px solid gray',
                          }}
                          onClick={() => setOpenModalType('DeleteConfirm')}
                        >
                          TRASH&nbsp;&nbsp;<i className="fa-solid fa-trash"></i>
                        </button>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          marginTop: '15px',
                          width: '100%',
                          marginBottom: '5px',
                        }}
                      >
                        <button
                          className="FEN-buttons reg-buttons green-hover"
                          onClick={() => setOpenModalType('FENBuilder')}
                          style={{
                            flex: '1',
                            border: '3px solid #57b047',
                            color: '#57b047',
                            height: '70px',
                            fontSize: '20px',
                          }}
                        >
                          PGN UPLOAD & BOARD EDITOR
                        </button>
                        {/* <button
                          className="FEN-buttons reg-buttons green-hover"
                          style={{
                            flex: '1',
                            border: '3px solid #57b047',
                            color: '#57b047',
                            height: '70px',
                            fontSize: '20px',
                          }}
                          onClick={() => {
                            // Get the FEN of the current position being viewed, not just the game state
                            let currentPositionFen;

                            if (currentMoveIdx === '-1') {
                              // If at the starting position, use the root FEN
                              currentPositionFen = rootFen;
                            } else {
                              // If viewing a move, get that move's resulting position
                              // We need to create a temporary game and replay moves up to the current index
                              const tempGame = new Chess(rootFen);
                              const moveHistory = game.history({ verbose: true });

                              // Replay moves up to the current index
                              for (let i = 0; i <= parseInt(currentMoveIdx); i++) {
                                if (i < moveHistory.length) {
                                  try {
                                    tempGame.move(moveHistory[i]);
                                  } catch (err) {
                                    console.error("Error replaying move:", err);
                                  }
                                }
                              }

                              currentPositionFen = tempGame.fen();
                            }


                            setOpenModalType('StartPosition');
                            // Store the current position FEN for the modal to use
                            localStorage.setItem('currentPositionFen', currentPositionFen);
                          }}
                        >
                          SET AS START POSITION
                        </button> */}
                      </div>
                    </div>
                  ) : null}

                  {/* Add Delete Puzzle button when in puzzle mode (since control panel is hidden) */}
                  {gameMetadata.gameMode === 'Puzzle' && (
                    <div style={{ width: '96%', marginBottom: '15px' }}>
                      <button
                        className="FEN-buttons reg-buttons"
                        onClick={() => setOpenModalType('DeleteConfirm')}
                        style={{
                          fontSize: '35px',
                          border: '3px solid #ff4444',
                          color: '#ff4444',
                          background: 'transparent',
                          width: '100%',
                          height: '80px',
                          borderRadius: '5px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Delete Puzzle&nbsp;&nbsp;
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  )}

                  {/* <div style={{
                    marginTop: showControls ? '10px' : '20px',
                    paddingBottom: '15px',
                    width: '96%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                                       

                    <Controls
                      game={game}
                      moves={moves}
                      setMoves={setMoves}
                      currentMove={currentMoveIdx}
                      setCurrentMove={setCurrentMoveIdx}
                      setPgn={setPgn}
                      showUndo
                      isAnnotationFocused={isAnnotationFocused}
                    />
                  </div> */}
                  <div
                    className=""
                    style={{ marginBottom: '5px', width: '96%' }}
                  >
                    <button
                      className="next-prev-button gold-hover-2"
                      onClick={() => setStep((s) => (s ? 0 : 1))}
                      style={{
                        color: 'gold',
                        border: '3px solid gold',
                        borderRadius: '5px',
                        background: 'gold',
                        fontSize: '35px',
                        height: '100px',
                        zIndex: 999,
                        marginBottom: '5px',
                        width: '100%',
                        display: 'block',
                        background: 'transparent',
                      }}
                    >
                      Share To ChessThread&nbsp;{' '}
                      <i className="fa-solid fa-paper-plane"></i>
                    </button>
                  </div>
                </div>

                {/* Conditional header: Show Main Line header for normal mode, PVLinesDisplay for puzzle mode */}
                {!isPuzzleMode ? (
                  /* Normal mode: Show Main Line/Analysis Board header with fa-chessboard */
                  <div
                    style={{
                      fontSize: '40px',
                      fontWeight: 'bold',
                      marginBottom: '15px',
                      textAlign: 'center',
                      borderBottom: '2px solid gold',
                      paddingBottom: '10px',
                      color: 'gold',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {/* +++ Conditionally render title +++ */}
                    {/* {gameMetadata?.gameMode === 'Puzzle' ? 'Puzzle Solution' : 'Game PGN'} */}

                    {/* Display opening information next to the title */}
                    {openingInfo && openingInfo.name ? (
                      <div
                        className="opening-info"
                        style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          color: 'gold',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          transition: 'color 0.2s ease',
                        }}
                        title="Openings provided by LiChess.org"
                        onClick={() =>
                          window.open('https://lichess.org', '_blank')
                        }
                        onMouseEnter={(e) => (e.target.style.color = '#ffdd44')}
                        onMouseLeave={(e) => (e.target.style.color = 'gold')}
                      >
                        {openingInfo.name}
                      </div>
                    ) : gameMetadata?.gameMode === 'Puzzle' ? (
                      'Puzzle Builder'
                    ) : (
                      'Analysis Board'
                    )}
                    {/* {!openingInfo && gameMetadata?.gameMode === 'Puzzle' ? 'Puzzle Solution' : 'Game PGN'} */}

                    {/* Add clickable chess-board icon */}
                    <i
                      ref={boardIconRef}
                      className="fa-solid fa-chess-board"
                      style={{
                        position: 'absolute',
                        left: '20px',
                        top: '50%',
                        transform: 'translateY(-65%)',
                        fontSize: '36px',
                        cursor: 'pointer',
                        // color: getRobotColor(),
                        color: showControlPanel ? '#00f2ea' : '#57b047',
                        transition: 'all 0.3s ease',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowControlPanel(!showControlPanel);
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#00f2ea';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = showControlPanel
                          ? '#00f2ea'
                          : '#57b047';
                      }}
                    />

                    {/* Control panel for chess options */}
                    {showControlPanel && (
                      <div
                        ref={controlPanelRef}
                        style={{
                          position: 'absolute',
                          bottom: '5px',
                          left: '70px',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-around',
                          gap: '15px',
                          backgroundColor: 'rgba(0, 0, 0, 1)',

                          borderRadius: '8px',
                          border: '2px solid gold',
                          zIndex: 100,
                          boxShadow: '0 0 15px rgba(0, 0, 0, 0.8)',
                          alignItems: 'center',
                          height: '70px',
                          width: 'calc(100% - 150px)',
                        }}
                      >
                        {/* ENGINE Section */}

                        {/* <div
                        className="engine-panel-btn"
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                      >
                        <div
                          onClick={handleStockfishIconsToggle}
                          style={{
                            cursor: 'pointer',
                            padding: '5px',
                            borderRadius: '50%',
                            boxShadow: localShowStockfishIcons
                              ? '0 0 8px rgba(76, 175, 80, 0.8)'
                              : '0 0 5px rgba(255, 215, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: localShowStockfishIcons ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            transition: 'all 0.3s ease',
                            height: '40px',
                            width: '40px',
                          }}
                        >
                          {localShowStockfishIcons ?
                            <img src={BrilliantGray} alt="Brilliant Icon" style={{ width: '30px', height: '30px' }} /> :
                            <img src={BrilliantBlue} alt="Brilliant Icon" style={{ width: '30px', height: '30px' }} />}
                        </div>
                        <div className="engine-panel-tooltip">
                          Move Classificaions 
                        </div>
                      </div> */}

                        {/* Stockfish Engine */}
                        <div
                          className="engine-panel-btn"
                          style={{
                            position: 'relative',
                            display: 'inline-block',
                          }}
                        >
                          <i
                            className="fa-solid fa-robot"
                            style={{
                              cursor: 'pointer',
                              color: localShowStockfish
                                ? 'rgb(76, 175, 80)'
                                : '#f44336',

                              padding: '5px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: localShowStockfish
                                ? '0 0 8px rgba(76, 175, 80, 0.8)'
                                : '0 0 5px rgba(255, 215, 0, 0.3)',
                              background: localShowStockfish
                                ? 'rgba(76, 175, 80, 0.1)'
                                : 'transparent',
                              fontSize: '24px',
                              transition: 'all 0.3s ease',
                              height: '40px',
                              width: '40px',
                            }}
                            onClick={handleStockfishToggle}
                          ></i>
                          <div className="engine-panel-tooltip">
                            Stockfish Engine
                          </div>
                        </div>

                        {/* Stockfish Eval Bar */}
                        <div
                          className="engine-panel-btn"
                          style={{
                            position: 'relative',
                            display: 'inline-block',
                          }}
                        >
                          <i
                            className="fa-solid fa-chart-simple "
                            style={{
                              cursor: 'pointer',
                              color: localShowStockfishEval
                                ? 'rgb(76, 175, 80)'
                                : '#f44336',
                              padding: '5px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: localShowStockfishEval
                                ? '0 0 8px rgba(76, 175, 80, 0.8)'
                                : '0 0 5px rgba(255, 215, 0, 0.3)',
                              background: localShowStockfishEval
                                ? 'rgba(76, 175, 80, 0.1)'
                                : 'transparent',
                              fontSize: '24px',
                              transition: 'all 0.3s ease',
                              height: '40px', // Fixed height to match the Brilliant icon
                              width: '40px', // Fixed width to match the Brilliant icon
                            }}
                            onClick={handleStockfishEvalToggle}
                          ></i>
                          <div className="engine-panel-tooltip">
                            Evaluation Bar
                          </div>
                        </div>

                        {/* Arrows toggle button - always on in AddGame */}
                        <div
                          className="engine-panel-btn"
                          style={{
                            position: 'relative',
                            display: 'inline-block',
                          }}
                        >
                          <i
                            className="fa-solid fa-arrow-up"
                            style={{
                              cursor: 'pointer',
                              color: 'rgb(76, 175, 80)', // Always green because always ON
                              padding: '5px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 0 8px rgba(76, 175, 80, 0.8)', // Always has glow because always ON
                              background: 'rgba(76, 175, 80, 0.1)',
                              fontSize: '24px',
                              transition: 'all 0.3s ease',
                              height: '40px',
                              width: '40px',
                            }}
                          ></i>
                          <div className="engine-panel-tooltip">My Arrows</div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Puzzle mode: Show PVLinesDisplay component instead of header */
                  <div style={{ marginBottom: '5px' }}>
                    <PVLinesDisplay
                      analysis={sapLiveAnalysis}
                      analysisData={sapLiveAnalysis}
                      isAnalyzing={sapIsCalculating}
                      isEngineReady={sapEngineReady}
                      fen={sapLiveAnalysis?.positionFEN || currentFen}
                      onMoveSelect={(moveData) => {
                        // Handle puzzle solution move selection
                        // console.log('Puzzle solution move selected:', moveData);

                        if (
                          moveData.movesToPlay &&
                          moveData.movesToPlay.length > 0
                        ) {
                          // Apply the sequence of moves to the board
                          moveData.movesToPlay.forEach((move, index) => {
                            setTimeout(() => {
                              window.dispatchEvent(
                                new CustomEvent('stockfish-move-selected', {
                                  detail: {
                                    move: move.lan,
                                    san: move.san,
                                    from: move.from,
                                    to: move.to,
                                    isSequentialPlayback: true,
                                    sequenceIndex: index,
                                    totalInSequence:
                                      moveData.movesToPlay.length,
                                  },
                                }),
                              );
                            }, index * 500); // 500ms delay between moves
                          });
                        }
                      }}
                      showLineSelector={false}
                      maxLines={3}
                    />
                  </div>
                )}

                {/* Show placeholders only when no moves AND not puzzle mode (puzzle mode has PVLinesDisplay instead) */}
                {moves?.length === 0 && gameMetadata.gameMode !== 'Puzzle' && (
                  <div className="no-moves-container">
                    {gameMetadata.gameMode === 'Lesson' || isLesson() ? (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>
                        Make The First Move
                        <br />
                        To Customize Your Lesson.
                      </div>
                    ) : isChallenge() ? (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>
                        Click Save & Share
                        <br />
                        To Publish Your Challenge.
                      </div>
                    ) : (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>
                        Make the first move,
                        <br />@{props?.user?.credentials?.handle}.
                      </div>
                    )}
                  </div>
                )}

                {/* Show MovesThree component - displays actual moves made (works alongside PVLinesDisplay in puzzle mode) */}

               
                <div
                  style={{
                    width: '535px',
                    // height: '100%', Caused issues
                    height:
                      gameMetadata.gameMode === 'Puzzle' ? '190px' : '100%',
                    borderRadius: '10px',
                    overflowY: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'gold transparent',
                
                    display: 'flex',
                    // justifyContent: 'center',
                    flexDirection: gameMetadata.gameMode === 'Puzzle' ? 'column' : '',
                    backgroundColor: gameMetadata.gameMode === 'Puzzle' ? '#141414f2' : '',
                    border: gameMetadata.gameMode === 'Puzzle' ? '1px solid #444' : 'none',
                  }}
                >
                   {gameMetadata.gameMode === "Puzzle" && (
                    <div style={{
                      width: '100%', 
                      textAlign: 'center',
                      marginBottom: '8px',
                      paddingBottom: '8px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      boxSizing: 'border-box',
                    }}>
                  <h3 style={{ 
                    color: '#ffd700', 
                    margin: '0',
                    fontSize: '18px', 
                    fontWeight: 'bold',
                    textAlign: 'center',
                    width: '100%',
                    borderBottom: '1px solid #444',
                    paddingBottom: '8px',
                    paddingTop: '16px',
                  }}>
                    My Puzzle Solution
                  </h3>
                  {moves?.length === 0 && (
                    <p style={{color: '#555', fontSize: '14px', textAlign: 'center', margin: '0', padding: '0 16px', marginTop: '50px'}}>Make moves or click the engine suggestions to set puzzle solution.</p>
                  )}
                  </div>
                  )}
                  

                  <MovesThree
                    isViewGame={false}
                    moveContent={moveContent}
                    setMoveContent={setMoveContent}
                    variationContent={variationContent}
                    setVariationContent={setVariationContent}
                    updateContentForMove={updateContentForMove}
                    updateVariationContent={updateVariationContent}
                    isAnnotationFocused={isAnnotationFocused}
                    setIsAnnotationFocused={setIsAnnotationFocused}
                    setSkipMoveIndexUpdate={setSkipMoveIndexUpdate}
                    isMoveInVariation={isMoveInVariation}
                    currentMoveIdx={currentMoveIdx}
                    starredMoves={starredMoves}
                    dispatch={dispatch}
                    startingTurn={boardStartingTurn}
                    annotationStyleOverride={
                      gameMetadata.gameMode === 'Puzzle'
                        ? { height: '28px' }
                        : null
                    }
                  />
                  
                </div>
                {gameMetadata.gameMode === "Puzzle" && (
                  <div style={{ marginTop: '35px' }}>
                  <ControlsThree
                          undo={true}
                          disableHotkeys={isAnnotationFocused}
                        />
                        </div>
                  )}

                {/* Show PVLinesDisplay as supplementary engine suggestions in puzzle mode */}
                {/* {gameMetadata.gameMode === "Puzzle" && (
                  <div className="puzzle-suggestions-container" style={{ marginBottom: '10px' }}>
                    <PVLinesDisplay
                      analysis={sapLiveAnalysis}
                      analysisData={sapLiveAnalysis}
                      isAnalyzing={sapIsCalculating}
                      isEngineReady={sapEngineReady}
                      fen={sapLiveAnalysis?.positionFEN || currentFen}
                      onMoveSelect={(moveData) => {
                        // Handle puzzle solution move selection
                        console.log('Puzzle solution move selected:', moveData);
                        
                        if (moveData.movesToPlay && moveData.movesToPlay.length > 0) {
                          // Play moves sequentially using the existing board event system
                          moveData.movesToPlay.forEach((move, index) => {
                            setTimeout(() => {
                              if (move.lan) {
                                // Use the existing stockfish-move-selected event that 3Board.jsx listens for
                                window.dispatchEvent(new CustomEvent('stockfish-move-selected', {
                                  detail: { 
                                    move: move.lan,
                                    skipAnalysis: index < moveData.movesToPlay.length - 1, // Skip analysis for all but last move
                                    isSequentialMove: true,
                                    sequenceIndex: index,
                                    totalMoves: moveData.movesToPlay.length
                                  }
                                }));
                              }
                            }, index * 750); // 750ms delay between moves
                          });
                        }
                      }}
                      showLineSelector={false}
                      maxLines={3}
                    />
                  </div>
                )} */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    positition: 'absolute',
                    width: '535px',
                    height: 'calc(100% - 0px)',
                    scrollbarWidth: 'none',
                    scrollbarColor: 'transparent transparent',
                  }}
                >
                  {/* Variation Caption - Now properly implemented with position-safe updates */}
                  {/* {isMoveInVariation(state.currentMove, moves) && (
                    <textarea 
                      placeholder="Add variation caption here..."
                      style={{
                        width: '490px',
                        height: '28px',
                        marginLeft: '10px',
                        marginTop: '10px',
                        padding: '10px',
                        backgroundColor: '#222',
                        color: 'white',
                        border: '2px solid gold',
                        borderRadius: '5px',
                        fontSize: '18px',
                        resize: 'none',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'gold #222',
                      }}
                      value={variationContent}
                      onChange={(e) => {
                        // Set skipMoveIndexUpdate to true before changing content
                        setSkipMoveIndexUpdate(true);
                        setIsAnnotationFocused(true);
                        
                        const newContent = e.target.value;
                        setVariationContent(cleanProfanity(newContent));
                        
                        // Update the variation content in moves array immediately
                        updateVariationContent(newContent);
                      }}
                      onBlur={() => {
                        // Use a timeout to prevent immediate processing of move changes
                        setTimeout(() => {
                          setIsAnnotationFocused(false);
                          setSkipMoveIndexUpdate(false);
                        }, 200);
                      }}
                      onFocus={() => {
                        setIsAnnotationFocused(true);
                        setSkipMoveIndexUpdate(true);
                      }}
                      onKeyDown={(e) => {
                        // Directly stop propagation for arrow keys and spacebar
                        if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                          // Allow the default behavior (cursor movement) but prevent the event from bubbling up
                          e.stopPropagation();
                        }
                      }}
                    />
                  )} */}

                  {/* Old Moves component - kept for reference */}
                  {/* <Moves
                    moves={moves}
                    starredMoves={starredMoves}
                    currentMove={currentMoveIdx}
                    // setCurrentMove={setCurrentMoveIdx}
                    setCurrentMove={handleMoveClick}
                    gameResultInfo={gameResultInfo}
                    currentTurn={game?.history({ verbose: true })?.[0]?.color}
                    className={""}
                    startingTurn={boardStartingTurn}
                  /> */}

                  {!showControls &&
                    moves?.length > 0 &&
                    gameMetadata.gameMode !== 'Puzzle' && (
                      <div
                        style={{
                          marginTop: showControls ? '10px' : '20px',
                          paddingBottom: '15px',
                          width: '540px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          position: 'absolute',
                          // bottom: '310px',
                          bottom: '205px',
                          left: '12px',
                          zIndex: 10,
                          opacity: moves?.length > 0 ? 1 : showControls ? 1 : 0,
                        }}
                        className=""
                      >
                        <ControlsThree
                          undo={true}
                          disableHotkeys={isAnnotationFocused}
                        />

                        {/* <Controls
                        game={game}
                        moves={moves}
                        setMoves={setMoves}
                        currentMove={currentMoveIdx}
                        // setCurrentMove={setCurrentMoveIdx}
                        handleCurrentMoveUpdate={handleMoveClick}
                        setPgn={setPgn}
                        showUndo
                        isAnnotationFocused={isAnnotationFocused}
                      /> */}
                      </div>
                    )}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              {/* First bordered group - Moves and Annotation */}
              <div
                style={{
                  border: '5px solid #121212',
                  borderRadius: '5px',
                  padding: '15px',
                  backgroundColor: 'black',
                  height: '100%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '',
                  background:
                    'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    bottom: '0px',
                    left: '0',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <div
                    className="tooltip-container"
                    style={{
                      width: '96%',
                      position: 'relative',
                      marginBottom: '15px',
                    }}
                  >
                    <button
                      className="publish-buttons"
                      style={{
                        opacity: isFormValid() ? 1 : 0.5,
                        cursor: isFormValid() ? 'pointer' : 'not-allowed',
                        display: 'block',
                        height: '100px',
                        fontSize: '35px',
                        border: '5px solid #ff0050',
                        borderRadius: '5px',
                      }}
                      onClick={() => {
                        if (isFormValid()) {
                          onSubmitGame();
                        } else {
                          // Show validation message, although the Publish Button ususally prevents invalid submissions.
                          if (tags.length === 0) {
                            alert('Please add at least 1 tag');
                          } else if (tags.length > 2) {
                            alert('Maximum 2 tags allowed');
                          } else if (gameCaption.trim().length < 2) {
                            alert(
                              'Please add a caption (minimum 2 characters)',
                            );
                          } else if (!isChallenge() && moves.length === 0) {
                            // Only show this message for non-Challenge games
                            alert('Please add at least one move');
                          }
                        }
                      }}
                      disabled={!isFormValid()}
                    >
                      P U B L I S H&nbsp;&nbsp;&nbsp;&nbsp;G A M E
                    </button>
                    {!isFormValid() && (
                      <div
                        className="tooltip publish-tooltip"
                        style={{
                          textAlign: 'left',
                          padding: '5px 15px',
                          border: '3px solid #ff0050',
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bottom: '110px',
                          width: '500px',
                          fontSize: '20px',
                          zIndex: 1005,
                          backgroundColor: 'black',
                          borderRadius: '8px',
                          paddingLeft: '10px',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
                        }}
                      >
                        <strong>Requirements to Publish:</strong>
                        <ul style={{ marginTop: '5px', marginBottom: '0' }}>
                          <li>- Most Game Modes Require Moves To Be Made</li>

                          <li>- Add Title & 1-2 Tags</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <button
                    className="next-prev-button gold-hover-2"
                    onClick={() => setStep((s) => (s ? 0 : 1))}
                    style={{
                      color: 'gold',
                      border: '3px solid gold',
                      borderRadius: '5px',
                      background: 'transparent',
                      fontSize: '35px',
                      height: '100px',
                      marginTop: '',
                      marginBottom: '10px',
                      width: '96%',
                      display: 'block',
                      position: 'relative',
                    }}
                  >
                    Return To Game PGN{' '}
                    <i className="fa-solid fa-chess-board"></i>
                  </button>
                </div>

                <div
                  style={{
                    fontSize: '40px',
                    fontWeight: 'bold',
                    marginBottom: '15px',
                    textAlign: 'center',
                    borderBottom: '2px solid gold',
                    paddingBottom: '10px',
                    color: 'gold',
                  }}
                >
                  Share Settings&nbsp;&nbsp;
                  <i className="fa-solid fa-paper-plane"></i>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: '10px',
                    marginRight: '10px',
                    marginTop: '20px',
                    marginBottom: '10px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      width: '15%',
                      color: gameCaption.length >= 2 ? 'white' : '#ff0050',
                    }}
                  >
                    Title:
                  </span>
                  <input
                    className="caption-input placeholder-red"
                    maxLength={1000}
                    style={{
                      marginTop: '',
                      color: 'white',
                      border: '1px solid white',
                      backgroundColor: 'black',
                      background: 'black',
                      width: '325px',
                    }}
                    value={gameCaption}
                    onChange={(e) =>
                      setGameCaption(cleanProfanity(e.target.value))
                    }
                    placeholder="Minimum 2 Characters..."
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: '10px',
                    marginRight: '10px',
                    marginTop: '10px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      width: '15%',
                      color: tags.length > 0 ? 'white' : '#ff0050',
                    }}
                  >
                    Tags:
                  </span>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginRight: '150px',
                    }}
                  >
                    <Select
                      className="tags-input"
                      options={
                        isOpeningSelectionMode
                          ? openingOptions
                          : Object.entries(TAGS)
                              .filter(([key, { label, sisterGroup }]) => {
                                // Hide The-Pros tag for non-admin/editor users (uses same pattern as puzzle hiding)
                                const user = props.user?.credentials || {};
                                // If it's The-Pros tag and user either has no role OR is not Admin/Editor
                                if (
                                  key === 'The-Pros' &&
                                  (!user.role ||
                                    (user.role !== 'Admin' &&
                                      user.role !== 'Editor'))
                                ) {
                                  return false;
                                }

                                // Always show the currently selected tags
                                if (tags.some((tag) => tag.value === key)) {
                                  return true;
                                }

                                // Filter out Puzzle and Challenge tags if not already selected
                                // They can only be added through GameModeChooserModal
                                if (
                                  key === 'Puzzle' &&
                                  !tags.some((tag) => tag.value === 'Puzzle')
                                ) {
                                  return false;
                                }

                                if (
                                  key === 'Challenge' &&
                                  !tags.some((tag) => tag.value === 'Challenge')
                                ) {
                                  return false;
                                }

                                // if (key === 'Lesson' && !tags.some(tag => tag.value === 'Lesson')) {
                                //   return false;
                                // }

                                // If no tags selected yet, show all options EXCEPT Puzzle, Challenge, and Lesson
                                if (!tags.length) {
                                  return (
                                    key !== 'Puzzle' && key !== 'Challenge'
                                  ); // && key !== 'Lesson'
                                }

                                // Filter out any tags that share a sister group with selected tags
                                const selectedSisterGroups = new Set(
                                  tags
                                    .map((tag) =>
                                      TAGS[tag.value]
                                        ? TAGS[tag.value].sisterGroup
                                        : null,
                                    )
                                    .filter((group) => group !== null),
                                );
                                return !selectedSisterGroups.has(sisterGroup);
                              })
                              .map(
                                ([
                                  key,
                                  { label, className, color, background },
                                ]) => ({
                                  value: key,
                                  label: label,
                                  className: className,
                                  color: color,
                                  background: background,
                                }),
                              )
                      }
                      isMulti={!isOpeningSelectionMode}
                      onChange={(newSelection) => {
                        if (isOpeningSelectionMode) {
                          // Handle opening selection
                          handleOpeningSelect(newSelection);
                        } else {
                          // Handle regular tag selection
                          if (newSelection.length <= 2) {
                            // Check if "Openings" tag was just selected
                            const hasOpenings = newSelection.some(
                              (tag) => tag.value === 'Openings',
                            );
                            const hadOpenings = tags.some(
                              (tag) => tag.value === 'Openings',
                            );

                            if (hasOpenings && !hadOpenings) {
                              // User just selected Openings tag
                              setTags(newSelection); // Update tags immediately
                              // Enter opening selection mode and fetch openings
                              setIsOpeningSelectionMode(true);
                              fetchOpeningsForGame();
                            } else {
                              // Normal tag update
                              setTags(newSelection);
                            }
                          } else {
                            // If trying to add a third tag, keep only the first two
                            setTags(newSelection.slice(0, 2));
                            // Show error indicator for 2 seconds
                            setShowTagError(true);
                            setTimeout(() => setShowTagError(false), 2000);
                          }
                        }
                      }}
                      value={isOpeningSelectionMode ? null : tags}
                      placeholder={
                        isOpeningSelectionMode
                          ? isLoadingOpenings
                            ? 'Loading openings...'
                            : 'Select an opening'
                          : 'Min: 1, Max: 2'
                      }
                      isClearable={false} // Disable the parent clear X button
                      styles={{
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          paddingLeft: '10px',
                          background: 'black',
                          color: '#ff0050', // Change placeholder to red
                        }),
                        control: (baseStyles, { isFocused }) => ({
                          ...baseStyles,
                          border: `1px solid ${
                            showTagError ? '#ff0050' : 'white'
                          }`, // Red border when error, otherwise white
                          backgroundColor: 'black', // Set background to black
                          boxShadow: 'none', // Remove the blue shadow on focus
                          '&:hover': {
                            border: `1px solid ${
                              showTagError ? '#ff0050' : 'white'
                            }`, // Remove hover color change
                          },
                          minHeight: '48px', // Set fixed height
                          maxHeight: '48px', // Lock maximum height
                          width: '400px', // Fixed width
                          maxWidth: '400px', // Maximum width
                        }),
                        multiValue: (baseStyles, { data }) => ({
                          ...baseStyles,
                          background: data.background,
                          borderRadius: '5px',
                        }),
                        multiValueLabel: (baseStyles, { data }) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          color: data.color,
                          background: data.background,
                        }),
                        multiValueRemove: (baseStyles, { data }) => ({
                          ...baseStyles,
                          color: data.color,
                          background: data.background,
                          ':hover': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            color: data.color,
                          },
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          width: 100,
                          height: 32,
                          maxHeight: 32,
                          display: 'flex',
                          alignItems: 'center',
                          color: 'white', // Set input text color to white
                          overflow: 'hidden', // Prevent overflow
                        }),
                        singleValue: (baseStyles) => ({
                          ...baseStyles,
                          color: 'white', // Change to white from black
                          marginTop: 2,
                        }),
                        option: (baseStyles, { data }) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          color: data.color,
                          background: data.background,
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          backgroundColor: 'black', // Set dropdown menu background to black
                          width: '250px', // Match control width
                          maxWidth: '250px',
                        }),
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxHeight: '38px', // Match the control height
                          overflow: 'hidden', // Hide overflow content
                        }),
                        container: (baseStyles) => ({
                          ...baseStyles,
                          height: '38px', // Fixed container height
                          width: '250px', // Fixed width
                          maxWidth: '250px', // Maximum width
                        }),
                        dropdownIndicator: (baseStyles) => ({
                          ...baseStyles,
                          paddingRight: 20, // Move the dropdown arrow away from edge
                        }),
                      }}
                    />
                  </div>
                </div>
                {/* <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      gap: '20px',
                      marginTop: '15px',
                      marginBottom: '20px',
                      marginLeft: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        width: '',
                        color: 'white',
                      }}
                    >
                      Players:
                    </span>
                    <input
                      className="caption-input-half"
                      placeholder="White"
                    />

                    <input
                      className="caption-input-half"
                      placeholder="Black"
                    /><span style={{ fontSize: '16px', color: 'gray' }}>(optional)</span>
                  </div> */}

                {/* Add Difficulty selection dropdown */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    marginLeft: '10px',
                    marginRight: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      className="tooltip-container"
                      style={{ position: 'relative' }}
                    >
                      <i
                        className="fa-solid fa-info-circle"
                        style={{
                          cursor: 'pointer',
                          color: 'gray',
                          fontSize: '16px',
                          marginRight: '10px',
                        }}
                      ></i>
                      <span
                        className="tooltip"
                        style={{
                          textAlign: 'left',
                          fontSize: '18px',
                          paddingBottom: '',
                          display: 'flex',
                          flexWrap: 'wrap',
                          left: '265px',
                          top: '-20px',
                          width: '465px' /* Adjusted for better readability */,
                          height: '40px' /* Let it adjust dynamically */,
                          color: 'gold',
                          overflow: 'hidden',
                          zIndex: 99995,
                          lineHeight: '1.4',
                          backgroundColor: 'black',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        Select the skill level for this content. This helps
                        users find appropriate content for their level.
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        width: '',
                        color: 'white',
                      }}
                    >
                      Level:
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <select
                      value={selectedDifficulty}
                      onChange={(e) => handleDifficultyChange(e.target.value)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '5px',
                        border: '1px solid white',
                        backgroundColor: 'black',
                        color: 'white',
                        fontSize: '16px',
                        width: '250px',
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                    >
                      {Object.keys(DIFFICULTY).map((key) => (
                        <option key={key} value={key}>
                          {DIFFICULTY[key].label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Add Bookmark Folder selection */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    marginLeft: '10px',
                    marginRight: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      className="tooltip-container"
                      style={{ position: 'relative' }}
                    >
                      <i
                        className="fa-solid fa-info-circle"
                        style={{
                          cursor: 'pointer',
                          color: 'gray',
                          fontSize: '16px',
                          marginRight: '10px',
                        }}
                      ></i>
                      <span
                        className="tooltip"
                        style={{
                          textAlign: 'left',
                          fontSize: '18px',
                          paddingBottom: '',
                          display: 'flex',
                          flexWrap: 'wrap',
                          left: '270px',
                          top: '-20px',
                          width: '465px' /* Adjusted for better readability */,
                          height: '40px' /* Let it adjust dynamically */,
                          zIndex: 999999,
                          lineHeight: '1.4',
                          color: 'gold',
                          background: 'black',
                          padding: '15px',
                          borderRadius: '8px',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {isCreatingNewFolder
                          ? 'After Publishing, a new folder will be created and this game will be added to it.'
                          : 'Optional: Save this game to a bookmark folder while publishing.'}
                      </span>
                    </div>
                    {!isCreatingNewFolder && (
                      <span
                        style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          width: '',
                          color: 'white',
                        }}
                      >
                        Bookmark:
                      </span>
                    )}
                  </div>

                  {!isCreatingNewFolder ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <select
                        value={selectedBookmarkFolder}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'NEW_FOLDER') {
                            setIsCreatingNewFolder(true);
                            setNewFolderName('');
                            setNewFolderPrivate(false);
                          } else {
                            setSelectedBookmarkFolder(value);
                            setIsCreatingNewFolder(false);
                          }
                        }}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '5px',
                          border: '1px solid white',
                          backgroundColor: 'black',
                          color: 'white',
                          fontSize: '16px',
                          width: '250px',
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="NONE">-- None --</option>
                        {userFolders.map((folder) => (
                          <option key={folder.name} value={folder.name}>
                            {folder.name}{' '}
                            {folder.isPrivate ? '(Private)' : '(Public)'}
                          </option>
                        ))}
                        <option value="NEW_FOLDER">+ Create New Folder</option>
                      </select>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: '10px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          width: '100%',
                          alignItems: 'center',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: 'white',
                            marginRight: '10px',
                          }}
                        >
                          Name:
                        </span>
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Enter new folder name"
                          maxLength="100"
                          style={{
                            padding: '10px',
                            borderRadius: '5px',
                            border: '1px solid white',
                            backgroundColor: 'black',
                            color: 'white',
                            fontSize: '16px',
                            flex: 0.93,
                            outline: 'none',
                          }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: '',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={newFolderPrivate}
                            onChange={() =>
                              setNewFolderPrivate(!newFolderPrivate)
                            }
                            id="new-folder-private"
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                          />
                          <label
                            htmlFor="new-folder-private"
                            style={{
                              fontSize: '16px',
                              color: 'white',
                              cursor: 'pointer',
                            }}
                          >
                            Private
                          </label>
                        </div>
                        <button
                          onClick={() => {
                            setIsCreatingNewFolder(false);
                            setSelectedBookmarkFolder('NONE');
                          }}
                          style={{
                            padding: '10px 15px',
                            borderRadius: '5px',
                            border: '1px solid #ff0050',
                            backgroundColor: 'transparent',
                            color: '#ff0050',
                            fontSize: '16px',
                            cursor: 'pointer',
                          }}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add Players & Ratings Button */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '20px',
                    marginBottom: '10px',
                  }}
                >
                  <button
                    onClick={handleAddPlayersAndRatings}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '8px',
                      border: playerPanelsExpanded
                        ? '2px solid #ff0000'
                        : '2px solid gold',
                      backgroundColor: 'transparent',
                      color: playerPanelsExpanded ? '#ff0000' : 'gold',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = playerPanelsExpanded
                        ? '#ff0000'
                        : 'gold';
                      e.target.style.color = playerPanelsExpanded
                        ? 'white'
                        : 'black';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = playerPanelsExpanded
                        ? '#ff0000'
                        : 'gold';
                    }}
                  >
                    {playerPanelsExpanded
                      ? 'Hide Players'
                      : 'Add Players & Ratings (Optional)'}
                  </button>
                </div>

                {selectedBookmarkFolder !== 'NONE' && (
                  <div
                    style={{
                      color: 'gray',
                      fontSize: '16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '2px',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    All games are publicly visible, regardless of their folder
                    privacy.
                    <br />
                    Private games, and more features coming soon!
                  </div>
                )}
                {/* <div
                    style={{
                      color: '',
                      fontWeight: '',
                      marginTop: '30px',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      marginLeft: '',
                    }}
                  >
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'gold', marginRight: '10px' }}>
                      @{props?.user?.credentials?.handle}
                    </span>
                    {VISIBILITIES.map((v) => {
                      const isSelected = gameMetadata.visibility === v;
                      return (
                        <button
                          key={v}
                          style={{
                            color: 'white',
                            border: '3px solid white',
                            opacity: isSelected ? '1' : '0.4',
                          }}
                          className={clsx(
                            'visibility-button',
                            isSelected && 'selected'
                          )}
                          onClick={() => {
                            const isPrivate = v === 'Private';
                            
                            setGameMetadata({
                              ...gameMetadata,
                              visibility: v,
                              isPrivate: isPrivate
                            });
                          }}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div> */}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Notification popup for bookmark operations */}
      {bookmarkNotification && (
        <div
          style={{
            position: 'absolute',
            right: '20px',
            bottom: '20px',
            backgroundColor:
              bookmarkNotification.type === 'error'
                ? 'rgba(255, 0, 0, 0.8)'
                : 'rgba(0, 128, 0, 0.8)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '5px',
            zIndex: 1001,
            fontSize: '14px',
            fontWeight: 'bold',
          }}
        >
          {bookmarkNotification.message}
        </div>
      )}
      {showUndoModal && (
        <UndoConfirmModal
          onClose={() => setShowUndoModal(false)}
          onConfirmUndo={() => {
            // Execute the undo logic with forceDelete=true to truncate the moves
            undoMove(true);
            setShowUndoModal(false);
          }}
          onJumpToEnd={() => {
            // Jump to the end of the move list
            jumpToLast();
            setShowUndoModal(false);
          }}
        />
      )}

      {/* Rate Limit Modal */}
      <RateLimitModal
        isVisible={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
      />
      
      {/* Starting Variation Limit Modal */}
      <StartingVariationLimitModal
        isOpen={showStartingVariationLimitModal}
        onClose={() => setShowStartingVariationLimitModal(false)}
      />
    </div>
  );
};

AddGame2.propTypes = {
  getScreams: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
});

export default connect(mapStateToProps, { getScreams })(AddGame2);
